import React, { useEffect, useState, useMemo, useCallback, useRef} from 'react';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import {
  ResponsiveContainer,
  ComposedChart,
  Line,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceDot,
  ReferenceLine,
  Label
} from 'recharts';
import { useDevice } from "./context/DeviceContext";
import { Sparklines, SparklinesLine } from "react-sparklines";
import { getCurrencySymbol } from "./utils";


const PAGE_SIZE = 10;
const timeFrames = {
  '1M': 22,
  '6M': 132,
  '1Y': 252,
  '3Y': 252 * 3,
  '5Y': 252 * 5,
  '10Y': 252 * 10,
  'Max': Infinity
};

const formatIndustryName = (str) => {
  if (!str) return '';
  return str.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
};

const addPadding = (min, max, pct = 0.05) => {
  const range = max - min || Math.abs(max) || 1; // safe for flat lines
  const pad = range * pct;
  return [min - pad, max + pad];
};

// Normalize any incoming date to "YYYY-MM-DD"
const toYMD = (d) => {
  if (!d) return d;
  // Handle "DD-MM-YYYY"
  if (/^\d{2}-\d{2}-\d{4}$/.test(d)) {
    const [DD, MM, YYYY] = d.split('-');
    return `${YYYY}-${MM}-${DD}`;
  }
  // Handle ISO like "2025-08-01T00:00:00Z"
  const dt = new Date(d);
  if (!isNaN(dt)) {
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const day = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
  return d; // already Y-M-D string
};

function getLastThursdayOfMonth(year, month1to12) {
  // month1to12: 1..12
  const lastDay = new Date(year, month1to12, 0); // last day of month
  const dayOfWeek = lastDay.getDay(); // 0=Sun..4=Thu..6=Sat
  // distance back to Thursday (4)
  const diff = (dayOfWeek - 4 + 7) % 7;
  const lastThu = new Date(year, month1to12 - 1, lastDay.getDate() - diff);
  return lastThu; // Date object
}

function shiftToPrevAvailableDate(dateObj, hasYMD, month1to12) {
  const d = new Date(dateObj);

  // don't drift into previous month endlessly
  const min = new Date(d.getFullYear(), month1to12 - 1, 1);

  while (d >= min) {
    const ymd = toYMD(d);
    if (hasYMD.has(ymd)) return ymd;
    d.setDate(d.getDate() - 1);
  }

  return null; // nothing found in that month in your data
}

/**
 * returns YYYY-MM-DD for that month’s *effective* expiry day
 * - last Thursday
 * - if holiday/weekend -> previous trading day (needs holiday set, weekend handled anyway)
 */
function getMonthlyExpiryYMDFromData(year, month1to12, hasYMD) {
  const lastThu = getLastThursdayOfMonth(year, month1to12);
  return shiftToPrevAvailableDate(lastThu, hasYMD, month1to12);
}

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  return isNaN(date) ? 'Invalid date' : date.toLocaleDateString('en-IN');
};

const formatDateTime = (dateStr) => {
  const date = new Date(dateStr);
  if (isNaN(date)) return "Invalid date";

  const day = date.toLocaleDateString("en-IN", {
    day: "2-digit",
  });

  const month = date.toLocaleDateString("en-IN", {
    month: "2-digit",
  });

  const year = date.getFullYear();

  const hours = date.getHours();
  const minutes = date.getMinutes();

  // 👇 Detect exactly 12:00 AM
  if (hours === 0 && minutes === 0) {
    return `${day}/${month}/${year} - closing`;
  }

  const time = date
    .toLocaleTimeString("en-IN", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
    .toLowerCase();

  return `${day}/${month}/${year} ${time}`;
};


const SeasonalityMatrix = React.memo(function PriceChart({
  chartData,
  stockInfo,
  isMobile
}) {
  const [showSeasonality, setShowSeasonality] = React.useState(false);

  const MONTH_KEYS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  const ALL_KEYS = [...MONTH_KEYS, "YEARLY"];

  const getCellBgStyle = (val) => {
    if (val == null || !Number.isFinite(val)) {
      return { backgroundColor: "#f3f4f6", color: "#6b7280" };
    }

    const abs = Math.min(Math.abs(val), 12);
    const alpha = 0.18 + (abs / 12) * 0.55;

    if (val > 0) {
      return {
        backgroundColor: `rgba(34,197,94,${alpha})`,
        color: "#14532d",
      };
    }

    if (val < 0) {
      return {
        backgroundColor: `rgba(239,68,68,${alpha})`,
        color: "#7f1d1d",
      };
    }

    return {
      backgroundColor: "#d1d5db",
      color: "#374151",
    };
  };

  const formatPctCell = (val) => {
    if (val == null || !Number.isFinite(val)) return "-";
    return `${val > 0 ? "+" : ""}${val.toFixed(1)}%`;
  };

  const seasonalityRows = React.useMemo(() => {
    if (!Array.isArray(chartData) || chartData.length === 0) return [];

    const valid = chartData
      .map((d) => {
        const dateObj = new Date(d.date);
        const close = Number(d.close);
        if (isNaN(dateObj) || !Number.isFinite(close)) return null;

        return {
          date: dateObj,
          close,
          year: String(dateObj.getFullYear()),
          monthIdx: dateObj.getMonth(),
          monthKey: MONTH_KEYS[dateObj.getMonth()],
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.date - b.date);

    if (!valid.length) return [];

    const bucket = new Map();

    for (const row of valid) {
      const key = `${row.year}-${row.monthIdx}`;
      if (!bucket.has(key)) bucket.set(key, []);
      bucket.get(key).push(row);
    }

    const monthSummaries = Array.from(bucket.entries())
      .map(([key, rows]) => {
        const first = rows[0];
        const last = rows[rows.length - 1];

        return {
          key,
          year: first.year,
          monthIdx: first.monthIdx,
          monthKey: first.monthKey,
          firstClose: Number(first.close),
          lastClose: Number(last.close),
          sortKey: first.date.getFullYear() * 100 + first.monthIdx,
        };
      })
      .sort((a, b) => a.sortKey - b.sortKey);

    const yearMap = new Map();

    for (let i = 0; i < monthSummaries.length; i++) {
      const cur = monthSummaries[i];
      const prev = i > 0 ? monthSummaries[i - 1] : null;

      let pct = null;

      if (
        prev &&
        Number.isFinite(prev.lastClose) &&
        prev.lastClose !== 0 &&
        Number.isFinite(cur.lastClose)
      ) {
        pct = ((cur.lastClose / prev.lastClose) - 1) * 100;
      }

      if (!yearMap.has(cur.year)) {
        yearMap.set(cur.year, {
          year: cur.year,
          firstClose: cur.firstClose,
          lastClose: cur.lastClose,
        });
      }

      const yearRow = yearMap.get(cur.year);

      yearRow[cur.monthKey] = pct;
      yearRow.lastClose = cur.lastClose;
    }

    for (const row of yearMap.values()) {
      if (
        Number.isFinite(row.firstClose) &&
        row.firstClose !== 0 &&
        Number.isFinite(row.lastClose)
      ) {
        row.YEARLY = ((row.lastClose / row.firstClose) - 1) * 100;
      } else {
        row.YEARLY = null;
      }

      delete row.firstClose;
      delete row.lastClose;
    }

    return Array.from(yearMap.values()).sort((a, b) => Number(b.year) - Number(a.year));
  }, [chartData]);

  const seasonalityRowsWithAvg = React.useMemo(() => {
    if (!Array.isArray(seasonalityRows) || seasonalityRows.length === 0) return [];

    const avgRow = { year: "AVG" };

    for (const key of ALL_KEYS) {
      const vals = seasonalityRows
        .map((row) => row[key])
        .filter((v) => v != null && Number.isFinite(v));

      avgRow[key] = vals.length
        ? vals.reduce((sum, v) => sum + v, 0) / vals.length
        : null;
    }

    return [avgRow, ...seasonalityRows];
  }, [seasonalityRows]);

  return (
    <div className="mt-2 mb-2">
      <label className="inline-flex items-center gap-2 text-xs sm:text-sm text-gray-600 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={showSeasonality}
          onChange={() => setShowSeasonality((v) => !v)}
          className="h-4 w-4 rounded border-gray-300"
          style={{ accentColor: "#2563eb" }}
        />
        <span className="font-normal">Seasonality Matrix</span>
      </label>

      {showSeasonality && seasonalityRows.length > 0 && (
        <div className="mt-3 rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-col gap-0.5 border-b border-gray-100 px-3 py-2 sm:px-4">
            {stockInfo && (
              <div className="flex justify-center items-center py-1">
                <h4 className="text-sm font-bold text-center">
                  {stockInfo.quoteType === "SECTOR"
                    ? formatIndustryName(stockInfo.longName)
                    : stockInfo.longName}
                </h4>
              </div>
            )}

            <div className="text-[11px] sm:text-xs text-gray-400">
              Monthly return = last close of month vs previous month close. Yearly return = last close of year vs first close of year.
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="min-w-max w-full border-collapse text-[11px] sm:text-xs text-gray-600">
              <thead>
                <tr className="bg-gray-50">
                  <th className="sticky left-0 z-20 bg-gray-50 px-3 py-2 text-left font-normal text-gray-500 border-b border-r border-gray-100">
                    YEAR
                  </th>

                  {ALL_KEYS.map((m) => (
                    <th
                      key={m}
                      className={`px-3 py-2 text-center font-normal text-gray-500 border-b border-gray-100 ${
                        m === "YEARLY"
                          ? "border-l-2 border-r-2 border-gray-400 font-semibold"
                          : ""
                      }`}
                    >
                      {m}
                    </th>
                  ))}
                </tr>
              </thead>

              <tbody>
                {seasonalityRowsWithAvg.map((row, idx) => (
                  <tr
                    key={row.year}
                    className={
                      row.year === "AVG"
                        ? "bg-blue-50 border-t-2 border-b-2 border-gray-500"
                        : idx % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50"
                    }
                  >
                    <td
                      className={`sticky left-0 z-10 px-3 py-2 text-gray-600 border-r border-b border-gray-100 ${
                        row.year === "AVG"
                          ? "bg-blue-50"
                          : idx % 2 === 0
                          ? "bg-white"
                          : "bg-gray-50"
                      }`}
                    >
                      <span className={row.year === "AVG" ? "text-blue-700" : ""}>
                        {row.year}
                      </span>
                    </td>

                    {ALL_KEYS.map((m) => {
                      const val = row[m];
                      const style = getCellBgStyle(val);

                      return (
                        <td
                          key={`${row.year}-${m}`}
                          className={`px-3 py-2 text-center border-b border-gray-100 ${
                            m === "YEARLY"
                              ? "border-l-2 border-r-2 border-gray-400 font-semibold"
                              : "font-normal"
                          }`}
                          style={style}
                        >
                          {formatPctCell(val)}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
});


import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export function StockArticle({ data, isMobile = false }) {
  if (!data?.markdown?.trim()) {
    return null;
  }

  return (
    <div className="mt-8">
      <h4 className="text-lg font-semibold">Key Points</h4>

      <div className="article-content mt-3">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            table: ({ children }) => (
              <div className="my-4">
                {isMobile && (
                  <div className="mb-1.5 text-right text-[11px] text-gray-400">
                    Swipe to view more →
                  </div>
                )}

                <div
                  className={`w-full overflow-x-auto rounded-lg border border-gray-200 bg-white ${
                    isMobile
                      ? "scrollbar-thin"
                      : "rounded-xl"
                  }`}
                >
                  <table
                    className={`border-collapse ${
                      isMobile
                        ? "min-w-[620px] text-xs"
                        : "w-full min-w-[800px] text-sm"
                    }`}
                  >
                    {children}
                  </table>
                </div>
              </div>
            ),

            thead: ({ children }) => (
              <thead className="bg-gray-100 text-gray-800">
                {children}
              </thead>
            ),

            tbody: ({ children }) => (
              <tbody className="divide-y divide-gray-200">
                {children}
              </tbody>
            ),

            tr: ({ children }) => (
              <tr className="transition-colors hover:bg-gray-50">
                {children}
              </tr>
            ),

            th: ({ children }) => (
              <th
                className={`border-r border-gray-200 text-left font-semibold text-gray-800 last:border-r-0 ${
                  isMobile
                    ? `
                      px-2 py-2
                      whitespace-normal
                      break-words
                      min-w-[90px]
                      first:sticky
                      first:left-0
                      first:z-20
                      first:min-w-[120px]
                      first:max-w-[150px]
                      first:bg-gray-100
                    `
                    : "px-4 py-3 whitespace-nowrap"
                }`}
              >
                {children}
              </th>
            ),

            td: ({ children }) => (
              <td
                className={`border-r border-gray-200 align-top text-gray-800 last:border-r-0 ${
                  isMobile
                    ? `
                      px-2 py-2
                      leading-5
                      whitespace-normal
                      break-words
                      min-w-[90px]
                      max-w-[180px]
                      first:sticky
                      first:left-0
                      first:z-10
                      first:min-w-[120px]
                      first:max-w-[150px]
                      first:bg-white
                      first:font-medium
                      first:text-gray-900
                    `
                    : "px-4 py-3"
                }`}
              >
                {children}
              </td>
            ),

            h1: ({ children }) => (
              <h1
                className={`font-bold text-gray-900 ${
                  isMobile
                    ? "mt-6 mb-3 text-xl"
                    : "mt-8 mb-4 text-2xl"
                }`}
              >
                {children}
              </h1>
            ),

            h2: ({ children }) => (
              <h2
                className={`font-bold text-gray-900 ${
                  isMobile
                    ? "mt-5 mb-2.5 text-lg"
                    : "mt-7 mb-3 text-xl"
                }`}
              >
                {children}
              </h2>
            ),

            h3: ({ children }) => (
              <h3
                className={`font-semibold text-gray-900 ${
                  isMobile
                    ? "mt-5 mb-2 text-base"
                    : "mt-6 mb-3 text-lg"
                }`}
              >
                {children}
              </h3>
            ),

            p: ({ children }) => (
              <p
                className={`mb-4 text-gray-700 ${
                  isMobile
                    ? "text-sm leading-6"
                    : "leading-7"
                }`}
              >
                {children}
              </p>
            ),

            ul: ({ children }) => (
              <ul
                className={`mb-4 list-disc text-gray-700 ${
                  isMobile
                    ? "space-y-1 pl-5 text-sm leading-6"
                    : "space-y-1.5 pl-6"
                }`}
              >
                {children}
              </ul>
            ),

            ol: ({ children }) => (
              <ol
                className={`mb-4 list-decimal text-gray-700 ${
                  isMobile
                    ? "space-y-1 pl-5 text-sm leading-6"
                    : "space-y-1.5 pl-6"
                }`}
              >
                {children}
              </ol>
            ),

            blockquote: ({ children }) => (
              <blockquote
                className={`my-4 border-l-4 border-gray-300 bg-gray-50 text-gray-600 ${
                  isMobile
                    ? "px-3 py-2 text-sm"
                    : "px-4 py-3"
                }`}
              >
                {children}
              </blockquote>
            ),

            code: ({ children, className }) => {
              const isCodeBlock = className?.startsWith("language-");

              if (isCodeBlock) {
                return (
                  <code className="block overflow-x-auto whitespace-pre rounded-lg bg-gray-900 p-4 text-sm text-gray-100">
                    {children}
                  </code>
                );
              }

              return (
                <code className="rounded bg-gray-100 px-1.5 py-0.5 text-[0.9em] text-gray-800">
                  {children}
                </code>
              );
            },

            a: ({ href, children }) => {
              const isExternal = href?.startsWith("http");

              return (
                <a
                  href={href}
                  target={isExternal ? "_blank" : undefined}
                  rel={isExternal ? "noopener noreferrer" : undefined}
                  className="font-medium text-blue-600 underline underline-offset-2 hover:text-blue-700"
                >
                  {children}
                </a>
              );
            },

            strong: ({ children }) => (
              <strong className="font-semibold text-slate-900">
                {children}
              </strong>
            ),
          }}
        >
          {data.markdown}
        </ReactMarkdown>
      </div>
    </div>
  );
}

const PriceChart = React.memo(function PriceChart({
  chartData,
  fullChartData,   // NEW
  stockInfo,
  isMobile,
  margin,
  xTicks,
  volumeTicks,
  priceTicks,
  yMin,
  yMax,
  decimalPoints,
  tickPrefix,
  newsData,
  onDotClick,
  getCloseByDate,
  activeDotKey,
  eventKey,
}) {
  const EVENT_LABELS = {
    news: "N",
    dividend: "D",
    result: "R",
    split: "S",
    default: "•",
  };

  // ✅ Toggle ATS line
  const [showATS, setShowATS] = React.useState(false);

  // ✅ Toggle OI line
  const [showOI, setShowOI] = React.useState(false);

  const [showMarginVolume, setShowMarginVolume] = React.useState(false);
  const [showHaircut, setShowHaircut] = React.useState(false);

  // ✅ Toggle Options PCR mini chart
  const [showPCR, setShowPCR] = React.useState(false);

  const [show50DMA, setShow50DMA] = React.useState(true);
  const [show200DMA, setShow200DMA] = React.useState(false);

  // Delivery stack check
  const hasDeliveryStack = chartData?.some(
    (d) => d.deliveredVol != null && d.undeliveredVol != null
  );

  // ✅ Detect if ATS is possible (needs trades > 0)
  const hasTrades = chartData?.some((d) => Number(d?.trades) > 0);

  const hasOI = chartData?.some(
    (d) => d?.oi != null && Number.isFinite(Number(d.oi))
  );

  const hasOptionsOI = chartData?.some(
    (d) =>
      d?.ceOi != null && Number.isFinite(Number(d.ceOi)) &&
      d?.peOi != null && Number.isFinite(Number(d.peOi))
  );

  const hasMarginVolume = chartData?.some(
    d => d.marginVolume != null &&
        Number.isFinite(Number(d.marginVolume))
  );

  const hasHaircut = chartData?.some(
    d => d.haircut != null &&
        Number.isFinite(Number(d.haircut))
  );

  // ✅ Add ATS into data (non-mutating)
  const dataWithATS = React.useMemo(() => {
    if (!Array.isArray(chartData) || chartData.length === 0)
      return chartData;

    return chartData.map((d) => {
      const vol = Number(d.volume ?? 0);
      const tr = Number(d.trades ?? 0);

      const ats =
        tr > 0 && Number.isFinite(vol)
          ? vol / tr
          : null;

      return {
        ...d,
        ats,
      };
    });
  }, [chartData]);

  const newsByDate = React.useMemo(() => {
    const map = new Map();

    if (!dataWithATS?.length || !newsData?.length) {
      return map;
    }

    newsData.forEach((ev) => {
      const match = getCloseByDate(dataWithATS, ev.date);

      if (match) {
        map.set(match.date, ev);
      }
    });

    return map;
  }, [dataWithATS, newsData, getCloseByDate]);



  // ✅ Build aligned options flow data (force 0s to avoid “alternate” look)
  const optFlowData = React.useMemo(() => {
    if (!Array.isArray(dataWithATS) || dataWithATS.length === 0) return [];

    // ✅ Build a set of dates that actually exist in your data
    const hasYMD = new Set(
      dataWithATS.map((d) => toYMD(d.date)).filter(Boolean)
    );

    // ✅ Cache monthly expiry ymd so we don't recompute per row
    const expiryCache = new Map(); // key: "YYYY-MM" => "YYYY-MM-DD" | null

    const getExpiryForRow = (ymd) => {
      const [Y, M] = ymd.split("-").map(Number);
      const key = `${Y}-${String(M).padStart(2, "0")}`;
      if (!expiryCache.has(key)) {
        expiryCache.set(key, getMonthlyExpiryYMDFromData(Y, M, hasYMD));
      }
      return expiryCache.get(key);
    };

    return dataWithATS.map((d, index, arr) => {
      const ce = Number(d?.ceOi);
      const pe = Number(d?.peOi);

      const ceOk = Number.isFinite(ce) ? ce : 0;
      const peOk = Number.isFinite(pe) ? pe : 0;

      const pcr = Number(d?.pcrOi);
      let pcrOk = Number.isFinite(pcr) ? pcr : null;

      // 👇 Detect last row
      const isLast = index === arr.length - 1;

      if (isLast) {
        const rawPCR = pcrOk != null ? pcrOk + 1 : null;

        // hide last PCR if invalid
        if ( (ceOk === 0 && peOk === 0) && (rawPCR === 0 || rawPCR === 1)) 
        {
          pcrOk = null; // 🚀 This removes last point & last line segment
        }
      }

      const ymd = toYMD(d.date);
      const expiryYMD = ymd ? getExpiryForRow(ymd) : null;

      return {
        date: d.date,
        peBar: peOk,
        ceBar: -ceOk,
        pcrOi: pcrOk,
        isMonthlyExpiry: expiryYMD != null && ymd === expiryYMD, // ✅ key flag
      };
    });
  }, [dataWithATS]);

  const optFlowDataByYMD = React.useMemo(() => {
    const m = new Map();
    for (const d of optFlowData) m.set(toYMD(d.date), d);
    return m;
  }, [optFlowData]);


  const OI_color = "#d2bf34ff";
  const ATS_color = "#f97316";
  const DMA50_color = "#ce2d5a";   // light yellow
  const DMA200_color = "#323435";  // light blue

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const closeEntry = payload.find((it) => it?.dataKey === "close");
    const p = (closeEntry?.payload || payload[0]?.payload) ?? {};

    const close = Number(p.close);
    const vol = Number(p.volume ?? 0);

    const delRaw = p.delivery;
    const del = delRaw == null ? null : Number(delRaw);

    const tradesRaw = p.trades;
    const trades = tradesRaw == null ? null : Number(tradesRaw);

    const ats = p.ats;
    const showATSVal =
      ats != null && Number.isFinite(ats) && trades != null && trades > 0;

    const showDel =
      del != null && Number.isFinite(del) && Number.isFinite(vol) && vol > 0;

    const delPct = showDel ? (del / vol) * 100 : null;

    // ✅ OI%
    const oiRaw = p.oi;
    const oi = oiRaw == null ? null : Number(oiRaw);
    const showOIVal = oi != null && Number.isFinite(oi);

    const fmtInt = (n) =>
      Number.isFinite(n) ? n.toLocaleString("en-IN") : "-";

    return (
      <div className="rounded-md border bg-white px-2.5 py-2 text-xs shadow">
        <div className="text-[11px] text-gray-500">{label}</div>

        <div className="mt-0.5 flex items-baseline gap-1">
          <span className="text-gray-500">Close:</span>
          <span className="font-semibold text-gray-900">
            {Number.isFinite(close) ? close.toFixed(2) : "-"}
          </span>
        </div>

        <div className="mt-1 flex items-baseline gap-1">
          <span className="text-gray-600">Vol:</span>
          <span className="text-gray-900">{fmtInt(vol)}</span>

          <span className="ml-1 text-gray-600">D:</span>
          <span className="text-gray-900">
            {showDel ? delPct.toFixed(1) + "%" : "-"}
          </span>
        </div>

        <div className="mt-1 text-gray-500">
          <span className="text-gray-600">Vol/Trade:</span>{" "}
          <span className="font-medium text-gray-900">
            {showATSVal ? ats.toFixed(0) : "-"}
          </span>
        </div>

        {showOIVal && (
          <div className="mt-1 text-gray-500">
            <span className="text-gray-600">OI%:</span>{" "}
            <span className="font-medium text-gray-900">
              {oi.toFixed(1)}%
            </span>
          </div>
        )}
      </div>
    );
  };

  const OptionsTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;

    const ceItem = payload.find((p) => p?.dataKey === "ceBar");
    const peItem = payload.find((p) => p?.dataKey === "peBar");
    const pcrItem = payload.find((p) => p?.dataKey === "pcrOi");

    const ceBar = ceItem?.value;
    const peBar = peItem?.value;
    const pcrNorm = pcrItem?.value;

    const ce = Number.isFinite(Number(ceBar)) ? Math.abs(Number(ceBar)) : null;
    const pe = Number.isFinite(Number(peBar)) ? Math.abs(Number(peBar)) : null;

    // You are plotting pcrOi = (PE/CE)-1, so raw PCR = pcrNorm + 1
    const rawPCR =
      pcrNorm != null && Number.isFinite(Number(pcrNorm)) ? Number(pcrNorm) + 1 : null;

    const fmtInt = (n) => (Number.isFinite(n) ? n.toLocaleString("en-IN") : "-");
    const fmtPcr = (n) => (Number.isFinite(n) ? n.toFixed(2) : "-");

    const bias =
      rawPCR == null
        ? ""
        : rawPCR > 1
        ? " (Put > Call)"
        : rawPCR < 1
        ? " (Call > Put)"
        : " (Neutral)";

    return (
      <div className="rounded-md border bg-white px-2.5 py-2 text-xs shadow">
        <div className="text-[11px] text-gray-500">{label}</div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">PE OI:</span>
          <span className="font-medium text-gray-900">{fmtInt(pe)}</span>
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">CE OI:</span>
          <span className="font-medium text-gray-900">{fmtInt(ce)}</span>
        </div>

        <div className="mt-1 flex items-baseline justify-between gap-3">
          <span className="text-gray-600">PCR:</span>
          <span className="font-semibold text-gray-900">
            {fmtPcr(rawPCR)}
            <span className="font-normal text-gray-500">{bias}</span>
          </span>
        </div>
      </div>
    );
  };

  // ✅ Consistent x ticks strategy for BOTH charts
  const alignedXTicks = isMobile ? xTicks.filter((_, i) => i % 2 === 0) : xTicks;

  // ✅ Fix plot width jitter by fixing axis widths
  const LEFT_AXIS_W = isMobile ? 44 : 54;
  const RIGHT_AXIS_W = isMobile ? 44 : 54;

  const pcrAbsMax = React.useMemo(() => {
    let m = 0;
    for (const d of optFlowData) {
      const v = Number(d?.pcrOi);
      if (Number.isFinite(v)) m = Math.max(m, Math.abs(v));
    }
    // fallback so axis never collapses
    return m > 0 ? m : 1;
  }, [optFlowData]);

  const flowAbsMax = React.useMemo(() => {
    if (!Array.isArray(optFlowData) || optFlowData.length === 0) return 0;

    let max = 0;
    for (const d of optFlowData) {
      const ce = Math.abs(Number(d?.ceBar) || 0);
      const pe = Math.abs(Number(d?.peBar) || 0);
      max = Math.max(max, ce, pe);
    }

    return max;
  }, [optFlowData]);
    
  return (
    <div className="-mx-4 sm:mx-0">
      {/* MAIN CHART */}
      <ResponsiveContainer width="100%" height={isMobile ? 300 : 400}>
        <ComposedChart
          data={dataWithATS}
          margin={margin}
          tabIndex={-1}
          focusable={false}
          syncId="priceSync"
        >
          <CartesianGrid stroke="#eae8e0ff" horizontal vertical={false} yAxisId="right" />

          <XAxis
            dataKey="date"
            ticks={alignedXTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
          />

          <YAxis
            yAxisId="left"
            orientation="left"
            width={LEFT_AXIS_W}
            ticks={volumeTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            label={
              !isMobile
                ? { value: "Volume", angle: -90, position: "insideLeft", fill: "#4f5763" }
                : undefined
            }
            tickFormatter={(value) => {
              if (value >= 1_000_000) return (value / 1_000_000).toFixed(1) + "M";
              if (value >= 1_000) return (value / 1_000).toFixed(0) + "K";
              return value;
            }}
          />

          <YAxis
            yAxisId="right"
            orientation="right"
            width={RIGHT_AXIS_W}
            ticks={priceTicks}
            tick={{ fontSize: isMobile ? 8 : 10, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            label={
              !isMobile
                ? { value: "Price", angle: 90, position: "insideRight", fill: "#4f5763" }
                : undefined
            }
            tickFormatter={(value) => value.toFixed(decimalPoints)}
          />

          <YAxis yAxisId="ats" width={0} orientation="right" hide domain={["auto", "auto"]} />
          <YAxis yAxisId="oi" width={0} orientation="right" hide domain={["auto", "auto"]} />

          <Tooltip content={<CustomTooltip />} />

          {hasDeliveryStack ? (
            <>
              <Bar
                yAxisId="left"
                dataKey="deliveredVol"
                stackId="vol"
                barSize={isMobile ? 10 : 20}
                fill="#87a1d6ff"
                isAnimationActive={false}
              />
              <Bar
                yAxisId="left"
                dataKey="undeliveredVol"
                stackId="vol"
                barSize={isMobile ? 10 : 20}
                fill="#BDD2FD"
                isAnimationActive={false}
              />
            </>
          ) : (
            <Bar
              yAxisId="left"
              dataKey="volume"
              barSize={isMobile ? 10 : 20}
              fill="#BDD2FD"
              isAnimationActive={false}
            />
          )}

          <Line
            yAxisId="right"
            type="monotone"
            dataKey="close"
            stroke="#3b82f6"
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />

          <Line
            yAxisId="right"
            dataKey="close"
            stroke="transparent"
            strokeWidth={0}
            isAnimationActive={false}
            activeDot={false}
            dot={(props) => {
              const { cx, cy, payload } = props;

              const ev = newsByDate.get(payload?.date);

              if (!ev) return null;

              // IMPORTANT:
              // Use chart/trading date for the active dot key.
              // Weekend news may have ev.date = Saturday,
              // while payload.date = Monday.
              const key = eventKey({
                ...ev,
                date: payload.date,
              });

              const isActive = key === activeDotKey;

              return (
                <g
                  transform={`translate(${cx}, ${cy - 5})`}
                  style={{ cursor: "pointer" }}
                  onClick={() =>
                    onDotClick({
                      type: ev.type,
                      date: payload.date,   // mapped chart/trading date
                      originalDate: ev.date, // actual news date
                      text: ev.details,
                      url: ev.url,
                      price: payload.close,
                    })
                  }
                >
                  <circle
                    r={10}
                    fill="transparent"
                    style={{ pointerEvents: "all" }}
                  />

                  <circle
                    r={8}
                    fill={isActive ? "#ef4444" : "#f59e0b"}
                    stroke={isActive ? "#111827" : "white"}
                    strokeWidth={isActive ? 3 : 2}
                  />

                  <text
                    textAnchor="middle"
                    dominantBaseline="central"
                    fontSize={8}
                    fill="white"
                  >
                    {EVENT_LABELS[ev.type] || EVENT_LABELS.default}
                  </text>
                </g>
              );
            }}
          />

          {show50DMA && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="dma50"
              stroke={DMA50_color}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}

          {show200DMA && (
            <Line
              yAxisId="right"
              type="monotone"
              dataKey="dma200"
              stroke={DMA200_color}
              strokeWidth={1.5}
              dot={false}
              connectNulls
              isAnimationActive={false}
            />
          )}

          {showATS && hasTrades && (
            <Line
              yAxisId="ats"
              type="monotone"
              dataKey="ats"
              stroke={ATS_color}
              strokeWidth={1.5}
              dot={false}
              isAnimationActive={false}
              connectNulls
            />
          )}

          {showOI && hasOI && (
            <Line
              yAxisId="oi"
              type="monotone"
              dataKey="oi"
              stroke={OI_color}
              strokeWidth={2}
              isAnimationActive={false}
              connectNulls
              dot={(props) => {
                const { cx, cy, payload } = props;
                if (!payload) return null;

                // Red dot
                const match = optFlowDataByYMD?.get(toYMD(payload.date));
                if (!match?.isMonthlyExpiry) return null;

                // ✅ require REAL OI (you said missing OI is stored as 0)
                const oiVal = Number(payload?.oi);
                if (!Number.isFinite(oiVal) || oiVal <= 0) return null;

                // ✅ (optional) also require options data exists (ce/pe)
                const ce = Number(match?.ceBar); // note: ceBar is negative
                const pe = Number(match?.peBar);
                const ceAbs = Number.isFinite(ce) ? Math.abs(ce) : 0;
                const peAbs = Number.isFinite(pe) ? Math.abs(pe) : 0;
                if (ceAbs === 0 || peAbs === 0) return null;


                return (
                  <circle
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill="#ef4444"
                    stroke="#111827"
                    strokeWidth={1}
                  />
                );
              }}
            />
          )}

        </ComposedChart>
      </ResponsiveContainer>



    {/* MINI MARGIN VOLUME + HAIRCUT PANEL */}
    {showMarginVolume && hasMarginVolume && (
      <div className="w-full">

        {/* Chart labels / explanation */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-2 pb-1 text-[10px] text-gray-600">

          <span className="flex items-center gap-1">
            <span
              className="inline-block h-2.5 w-2.5 rounded-sm"
              style={{ backgroundColor: "#8b5cf6" }}
            />
            Margin Volume (Shares)
          </span>

          {hasHaircut && (
            <span className="flex items-center gap-1">
              <span
                className="inline-block h-[2px] w-4"
                style={{ backgroundColor: "#f97316" }}
              />
              Haircut (%) — Collateral value deduction
            </span>
          )}

        </div>

        <ResponsiveContainer width="100%" height={140}>
          <ComposedChart
            data={dataWithATS}
            margin={margin}
            barCategoryGap="0%"
            syncId="priceSync"
          >
            <CartesianGrid
              stroke="#eae8e0ff"
              vertical={false}
            />

            <XAxis
              dataKey="date"
              ticks={alignedXTicks}
              tick={{
                fontSize: isMobile ? 10 : 8,
                fill: "#4f5763"
              }}
              axisLine={{ stroke: "#e5e7eb" }}
              tickLine={{ stroke: "#e5e7eb" }}
            />

            {/* LEFT AXIS: Margin Volume */}
            <YAxis
              yAxisId="margin"
              orientation="left"
              width={LEFT_AXIS_W}
              domain={[0, "auto"]}
              tick={{
                fontSize: 8,
                fill: "#4f5763"
              }}
              tickFormatter={(value) => {
                if (value >= 1_000_000)
                  return (value / 1_000_000).toFixed(1) + "M";

                if (value >= 1_000)
                  return (value / 1_000).toFixed(0) + "K";

                return value;
              }}
            />

            {/* RIGHT AXIS: Haircut Percentage */}
            {hasHaircut ? (
              <YAxis
                yAxisId="haircut"
                orientation="right"
                width={RIGHT_AXIS_W}
                domain={[0, 100]}
                tick={{
                  fontSize: 8,
                  fill: "#f97316"
                }}
                tickFormatter={(value) => `${value}%`}
                axisLine={false}
                tickLine={false}
              />
            ) : (
              /* Preserve right gutter alignment */
              <YAxis
                yAxisId="gutter"
                orientation="right"
                width={RIGHT_AXIS_W}
                tick={false}
                axisLine={false}
                tickLine={false}
              />
            )}

            {/* Combined Tooltip */}
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;

                const row = payload.find(
                  p => p?.payload
                )?.payload;

                if (!row) return null;

                const marginVolume = row.marginVolume;
                const haircut = row.haircut;

                return (
                  <div className="rounded-md border bg-white px-3 py-2 text-xs shadow">

                    <div className="mb-1 text-gray-500">
                      {label}
                    </div>

                    <div className="font-medium text-purple-600">
                      Margin Volume:{" "}
                      {marginVolume != null &&
                      Number.isFinite(Number(marginVolume))
                        ? Number(marginVolume).toLocaleString("en-IN")
                        : "-"}
                    </div>

                    {hasHaircut && (
                      <>
                        <div className="mt-1 font-medium text-orange-600">
                          Haircut:{" "}
                          {haircut != null &&
                          Number.isFinite(Number(haircut))
                            ? `${Number(haircut).toFixed(2)}%`
                            : "-"}
                        </div>
                      </>
                    )}

                  </div>
                );
              }}
            />

            {/* Margin Volume Bars */}
            <Bar
              yAxisId="margin"
              dataKey="marginVolume"
              name="Margin Volume"
              fill="#8b5cf6"
              barSize={isMobile ? 10 : 20}
              isAnimationActive={false}
            />

            {/* Haircut Line */}
            {hasHaircut && (
              <Line
                yAxisId="haircut"
                type="stepAfter"
                dataKey="haircut"
                name="Haircut (%)"
                stroke="#f97316"
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            )}

          </ComposedChart>
        </ResponsiveContainer>

      </div>
    )}


      {/* MINI OPTIONS PANEL */}
      {showPCR && hasOptionsOI && (
      <ResponsiveContainer width="100%" height={isMobile ? 140 : 140}>
        <ComposedChart
          data={optFlowData}
          margin={margin}          // ✅ same margins
          barCategoryGap="0%"
          barGap={0}
          stackOffset="sign"
          syncId="priceSync"       // ✅ sync with main chart
        >
          <CartesianGrid stroke="#eae8e0ff" vertical={false} />

          <XAxis
            dataKey="date"
            ticks={alignedXTicks}   // ✅ same ticks
            tick={{ fontSize: isMobile ? 10 : 8, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
          />

          <YAxis
            yAxisId="flow"
            orientation="left"
            width={LEFT_AXIS_W}     // ✅ same left gutter
            domain={[-flowAbsMax, flowAbsMax]}   // ✅ CRITICAL FIX
            tick={{ fontSize: isMobile ? 8 : 8, fill: "#4f5763" }}
            axisLine={{ stroke: "#e5e7eb" }}
            tickLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(v) => {
              const x = Math.abs(Number(v));
              if (x >= 1_000_000) return (x / 1_000_000).toFixed(1) + "M";
              if (x >= 1_000) return (x / 1_000).toFixed(0) + "K";
              return String(x);
            }}
          />

          {/* Top-left label: PE */}
          <ReferenceLine yAxisId="flow" y={flowAbsMax} stroke="transparent">
            <Label
              value="PE OI Volume"
              position="insideTopLeft"
              offset={4}
              fill="#E58F8C"      // or match your PE bar color
              fontSize={isMobile ? 10 : 11}
              fontWeight={600}
            />
          </ReferenceLine>

          {/* Bottom-left label: CE */}
          <ReferenceLine yAxisId="flow" y={-flowAbsMax} stroke="transparent">
            <Label
              value="CE OI Volume"
              position="insideBottomLeft"
              offset={4}
              fill="#0f766e"        // or match your CE bar color
              fontSize={isMobile ? 10 : 11}
              fontWeight={600}
            />
          </ReferenceLine>

          {/* ✅ Hidden PCR scale axis */}
          <YAxis
            yAxisId="pcr"
            orientation="right"
            hide
            domain={[-pcrAbsMax, pcrAbsMax]}   // ✅ always includes 0, always stable
            allowDataOverflow={true}
          />

          {/* ✅ THIS is the alignment fix: reserve the right-side gutter always */}
          <YAxis
            yAxisId="gutter"
            orientation="right"
            width={RIGHT_AXIS_W}
            tick={false}
            axisLine={false}
            tickLine={false}
          />

          <ReferenceLine yAxisId="flow" y={0} stroke="#111827" strokeWidth={1} />
          <Tooltip content={<OptionsTooltip />} />

          <Bar
            yAxisId="flow"
            dataKey="peBar"
            stackId="pc"
            fill="#E58F8C"
            barSize={isMobile ? 10 : 20}
            isAnimationActive={false}
          />

          <Bar
            yAxisId="flow"
            dataKey="ceBar"
            stackId="pc"
            fill="#73BDB6"
            barSize={isMobile ? 10 : 20}
            isAnimationActive={false}
          />

          <Line
            yAxisId="pcr"
            type="monotone"
            dataKey="pcrOi"
            stroke="#111827"
            strokeWidth={1.5}
            connectNulls
            isAnimationActive={false}
            dot={(props) => {
              const { cx, cy, payload } = props;
              if (!payload?.isMonthlyExpiry) return null;

              // ✅ require real options OI (since missing becomes 0)
              const ceAbs = Math.abs(Number(payload?.ceBar) || 0);
              const peAbs = Math.abs(Number(payload?.peBar) || 0);
              if (ceAbs === 0 || peAbs === 0) return null;

              // ✅ require PCR value to be real
              const pcrVal = Number(payload?.pcrOi);
              if (!Number.isFinite(pcrVal)) return null;

              return (
                <circle
                  cx={cx}
                  cy={cy}
                  r={4}
                  fill="#ef4444"
                  stroke="#111827"
                  strokeWidth={1}
                />
              );
            }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      )}

      {stockInfo && (
        <div className="flex justify-center items-center py-1">
          <h4 className="text-sm font-bold text-center">
            {stockInfo.quoteType === "SECTOR"
              ? formatIndustryName(stockInfo.longName)
              : stockInfo.longName}
          </h4>
        </div>
      )}

      {/* ✅ Toggles */}
      <div className="mt-2 overflow-x-auto">
        <div className="mx-auto w-fit px-2 space-y-2">

          {/* Row 1: 50 DMA, 200 DMA, ATS */}
          <div className="flex gap-3 whitespace-nowrap justify-center">

            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={show50DMA}
                onChange={() => setShow50DMA((v) => !v)}
                className="h-4 w-4 rounded border-gray-400"
                style={{ accentColor: DMA50_color }}
              />
              <span className="font-medium">50 DMA</span>
            </label>

            <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
              <input
                type="checkbox"
                checked={show200DMA}
                onChange={() => setShow200DMA((v) => !v)}
                className="h-4 w-4 rounded border-gray-400"
                style={{ accentColor: DMA200_color }}
              />
              <span className="font-medium">200 DMA</span>
            </label>

            {hasTrades && (
              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showATS}
                  onChange={() => setShowATS((v) => !v)}
                  className="h-4 w-4 rounded border-gray-400"
                  style={{ accentColor: ATS_color }}
                />

                <span>
                  <span className="font-medium">ATS</span>
                  <span className="hidden sm:inline"> (Volume/Trades)</span>
                </span>
              </label>
            )}

            {hasMarginVolume && (
              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showMarginVolume}
                  onChange={() => setShowMarginVolume(v => !v)}
                  className="h-4 w-4 rounded border-gray-400"
                  style={{ accentColor: "#8b5cf6" }}
                />

                <span className="font-medium">
                  Margin Volume
                </span>
              </label>
            )}

          </div>

          {/* Row 2: Fut OI, Options PCR */}
          <div className="flex gap-3 whitespace-nowrap justify-center">

            {hasOI && (
              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showOI}
                  onChange={() => setShowOI((v) => !v)}
                  className="h-4 w-4 rounded border-gray-400"
                  style={{ accentColor: OI_color }}
                />

                <span>
                  <span className="font-medium">Fut OI%</span>
                  <span className="hidden sm:inline"> (FutEq/MWPL)</span>
                </span>
              </label>
            )}

            {hasOptionsOI && (
              <label className="flex items-center gap-2 text-xs sm:text-sm text-gray-700 cursor-pointer shrink-0">
                <input
                  type="checkbox"
                  checked={showPCR}
                  onChange={() => setShowPCR((v) => !v)}
                  className="h-4 w-4 rounded border-gray-400"
                  style={{ accentColor: "#111827" }}
                />

                <span>
                  <span className="font-medium">Options PCR</span>
                  <span className="hidden sm:inline"> (CE/PE OI)</span>
                </span>
              </label>
            )}

          </div>

        </div>
      </div>

    </div>
  );

});



/**
 * Usage:
 * <NewsFeedTable data={newsFeedData} />
 */
const NewsFeedTable = ({ data = [] }) => {
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // "1D" | "1W" | null
  const [sortDir, setSortDir] = useState("desc");   // "asc" | "desc"

  const siteName = (linkOrHost) => {
    if (!linkOrHost) return "unknown";
    try {
      const host = linkOrHost.includes("://")
        ? new URL(linkOrHost).hostname
        : linkOrHost;
      const cleaned = host.replace(/^www\./i, "");
      return cleaned.split(".")[0] || cleaned;
    } catch {
      const cleaned = String(linkOrHost)
        .replace(/^https?:\/\//i, "")
        .replace(/^www\./i, "");
      return cleaned.split(".")[0] || "unknown";
    }
  };

  const formatDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d || "");
    return dt.toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "2-digit",
    });
  };

  const sorted = useMemo(() => {
    let items = [...(data || [])];
    if (sortField === "1D") {
      items.sort((a, b) => {
        const ca = parseFloat(a?.change_1D ?? 0);
        const cb = parseFloat(b?.change_1D ?? 0);
        return sortDir === "asc" ? ca - cb : cb - ca;
      });
    } else if (sortField === "1W") {
      items.sort((a, b) => {
        const ca = parseFloat(a?.change_1W ?? 0);
        const cb = parseFloat(b?.change_1W ?? 0);
        return sortDir === "asc" ? ca - cb : cb - ca;
      });
    } else {
      // default sort by published desc
      items.sort((a, b) => {
        const ta = new Date(a?.published || 0).getTime();
        const tb = new Date(b?.published || 0).getTime();
        return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
      });
    }
    return items;
  }, [data, sortField, sortDir]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const pageSlice = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return sorted.slice(start, start + PAGE_SIZE);
  }, [sorted, pageSafe]);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  const toggleSort = (field) => {
    if (sortField === field) {
      // toggle direction
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      // new field defaults to desc
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  };

  return (
    <div className="w-full mt-6">
      <h4 className="text-lg font-semibold">News & Stories</h4>

      {/* Top controls */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
        <div className="text-sm text-gray-500">
          Showing {pageSlice.length} of {sorted.length} items
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleSort("1D")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
          >
            1D%{" "}
            {sortField === "1D" ? (sortDir === "asc" ? "↑" : "↓") : ""}
          </button>
          <button
            onClick={() => toggleSort("1W")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
          >
            1W%{" "}
            {sortField === "1W" ? (sortDir === "asc" ? "↑" : "↓") : ""}
          </button>
          <button
            onClick={goPrev}
            disabled={pageSafe === 1}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm tabular-nums">
            Page {pageSafe} of {totalPages}
          </span>
          <button
            onClick={goNext}
            disabled={pageSafe === totalPages}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* Table wrapper */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <tbody className="divide-y">
            {pageSlice.length === 0 ? (
              <tr>
                <td colSpan={2} className="px-3 py-6 text-center text-gray-500">
                  No news found.
                </td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const title = row?.title || "(No title)";
                const link = row?.link || "#";
                const sourceLabel = siteName(row?.source || row?.link || "");
                const published = formatDate(row?.published);

                const change_1D = parseFloat(row?.change_1D ?? null);
                const change_1W = parseFloat(row?.change_1W ?? null);

                const changeText_1D =
                  change_1D != null && !isNaN(change_1D)
                    ? `1D ${change_1D.toFixed(2)}%`
                    : "";

                const changeText_1W =
                  change_1W != null && !isNaN(change_1W)
                    ? `1W ${change_1W.toFixed(2)}%`
                    : "";

                const changeClass_1D =
                  change_1D != null && !isNaN(change_1D)
                    ? change_1D >= 0
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-500";

                const changeClass_1W =
                  change_1W != null && !isNaN(change_1W)
                    ? change_1W >= 0
                      ? "text-green-600"
                      : "text-red-600"
                    : "text-gray-500";

                return (
                  <tr
                    key={idx}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-100"}
                  >
                    <td className="px-3 py-2">
                      <span className="font-light hover:decoration-solid break-words">
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={title}
                        >
                          {title}
                        </a>
                      </span>
                      <span className="text-xs text-gray-500 ml-2">
                        <a
                          href={row.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {sourceLabel}
                        </a>{" "}
                        ({published})
                      </span>
                      {changeText_1D && (
                        <span className={`text-xs ml-2 ${changeClass_1D}`}>
                          {changeText_1D}
                        </span>
                      )}
                      {changeText_1W && (
                        <span className={`text-xs ml-2 ${changeClass_1W}`}>
                          {changeText_1W}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const FinancialImpacts = ({ stockInfo, className = "" }) => {
  if (!stockInfo) return null;

  const parseList = (str = "", delim = "*") =>
    String(str)
      .split(delim)
      .map(s => s.trim())
      .filter(Boolean);

  const positives = parseList(stockInfo.positiveImpacts || "", "*");
  const negatives = parseList(stockInfo.negativeImpacts || "", "*");

  const renderItemWithSource = (text) => {
    // Extract source URL if present
    const match = text.match(/<([^>]+)>/);

    const url = match ? match[1] : null;

    // Remove source tag from displayed text
    const cleanText = text.replace(/<[^>]+>/g, "").trim();

    // Split on the FIRST colon
    const colonIndex = cleanText.indexOf(":");

    let label = "";
    let description = cleanText;

    if (colonIndex !== -1) {
      label = cleanText.substring(0, colonIndex + 1).trim();
      description = cleanText.substring(colonIndex + 1).trim();
    }

    return (
      <>
        {label ? (
          <>
            <strong className="font-semibold">{label}</strong>{" "}
            {description}
          </>
        ) : (
          cleanText
        )}

        {url && (
          <>
            {" "}
            <a
              href={url.startsWith("http") ? url : `https://${url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="ml-1 text-xs text-blue-500 underline"
            >
              [src]
            </a>
          </>
        )}
      </>
    );
  };

  return (
    <div className={`mt-6 ${className}`}>
      {/* Header with top margin */}
      <h4 className="text-lg font-semibold mb-2">
        Quick Report
      </h4>

      {/* Card */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {/* Two-column layout (stacks on mobile) */}
        <div className="grid grid-cols-1 md:grid-cols-2 items-stretch">
          {/* Positive column */}
          <div className="p-4 flex flex-col h-full border-b border-gray-200 md:border-b-0 md:border-r">
            <div className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
              <span aria-hidden>👍</span>
              <span>Positive</span>
            </div>

            {positives.length ? (
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-800">
                {positives.map((item, idx) => (
                  <li key={idx} className="break-words">
                  {renderItemWithSource(item)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-400 italic">
                No data available.
              </p>
            )}
          </div>

          {/* Negative column */}
          <div className="p-4 flex flex-col h-full">
            <div className="text-sm font-semibold text-gray-700 inline-flex items-center gap-2">
              <span aria-hidden>👎</span>
              <span>Negative</span>
            </div>

            {negatives.length ? (
              <ul className="mt-2 list-disc list-inside space-y-1 text-sm text-gray-800">
                {negatives.map((item, idx) => (
                  <li key={idx} className="break-words">
                  {renderItemWithSource(item)}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-2 text-sm text-gray-400 italic">
                No data available.
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const StockEvents = ({ data = [] }) => {
  const EVENT_PAGE_SIZE = 5;

  const [sortField, setSortField] = useState("Date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // --------------------------------------------------
  // Date helpers
  // --------------------------------------------------

  const parseEventDate = (value) => {
    if (!value) return null;

    // Handle YYYY-MM-DD without UTC timezone shifting
    if (typeof value === "string") {
      const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);

      if (match) {
        const [, year, month, day] = match;

        const parsed = new Date(
          Number(year),
          Number(month) - 1,
          Number(day)
        );

        if (
          parsed.getFullYear() === Number(year) &&
          parsed.getMonth() === Number(month) - 1 &&
          parsed.getDate() === Number(day)
        ) {
          return parsed;
        }

        return null;
      }
    }

    const parsed = new Date(value);

    if (isNaN(parsed.getTime())) return null;

    return new Date(
      parsed.getFullYear(),
      parsed.getMonth(),
      parsed.getDate()
    );
  };

  const getEventStatus = (dateValue) => {
    const eventDate = parseEventDate(dateValue);

    if (!eventDate) return null;

    const now = new Date();

    const today = new Date(
      now.getFullYear(),
      now.getMonth(),
      now.getDate()
    );

    if (eventDate.getTime() === today.getTime()) {
      return "Today";
    }

    if (eventDate.getTime() > today.getTime()) {
      return "Upcoming";
    }

    return null;
  };

  // --------------------------------------------------
  // Sorting
  // --------------------------------------------------

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  };

  const sortedEvents = useMemo(() => {
    const arr = [...(data || [])];

    if (sortField === "1D") {
      arr.sort((a, b) => {
        const av = parseFloat(a?.change_1D ?? 0);
        const bv = parseFloat(b?.change_1D ?? 0);

        return sortDir === "asc" ? av - bv : bv - av;
      });
    } else if (sortField === "1W") {
      arr.sort((a, b) => {
        const av = parseFloat(a?.change_1W ?? 0);
        const bv = parseFloat(b?.change_1W ?? 0);

        return sortDir === "asc" ? av - bv : bv - av;
      });
    } else {
      arr.sort((a, b) => {
        const ta = parseEventDate(a?.date)?.getTime() ?? 0;
        const tb = parseEventDate(b?.date)?.getTime() ?? 0;

        const diff = tb - ta;

        return sortDir === "asc" ? -diff : diff;
      });
    }

    return arr;
  }, [data, sortField, sortDir]);

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(sortedEvents.length / EVENT_PAGE_SIZE)
  );

  const pagedEvents = useMemo(() => {
    const start = (page - 1) * EVENT_PAGE_SIZE;

    return sortedEvents.slice(
      start,
      start + EVENT_PAGE_SIZE
    );
  }, [sortedEvents, page]);

  useEffect(() => {
    setPage(1);
  }, [data, sortField, sortDir]);

  // --------------------------------------------------
  // Percentage helpers
  // --------------------------------------------------

  const pctText = (v) =>
    v == null || String(v).trim() === "" || isNaN(Number(v))
      ? "—"
      : `${Number(v).toFixed(2)}%`;

  const pctClass = (v) =>
    v == null || String(v).trim() === "" || isNaN(Number(v))
      ? "text-gray-500"
      : Number(v) >= 0
      ? "text-green-600"
      : "text-red-600";

  const sortCaret = (field) =>
    sortField === field
      ? sortDir === "asc"
        ? "↑"
        : "↓"
      : "";

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div className="w-full mt-6">
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-lg font-semibold">
          Events
        </h4>

        {sortedEvents.length > EVENT_PAGE_SIZE && (
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() =>
                setPage((p) => Math.max(1, p - 1))
              }
              disabled={page === 1}
              className="px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>

            <span className="text-gray-500">
              {page} / {totalPages}
            </span>

            <button
              onClick={() =>
                setPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={page === totalPages}
              className="px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr>
              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("Date")}
                title="Sort by Date"
              >
                Date {sortCaret("Date")}
              </th>

              <th className="px-4 py-2 text-left border border-gray-200">
                Purpose
              </th>

              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("1D")}
                title="Sort by 1D change"
              >
                1D {sortCaret("1D")}
              </th>

              <th
                className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none"
                onClick={() => toggleSort("1W")}
                title="Sort by 1W change"
              >
                1W {sortCaret("1W")}
              </th>
            </tr>
          </thead>

          <tbody>
            {(!data || data.length === 0) ? (
              <tr>
                <td
                  colSpan={4}
                  className="px-4 py-6 text-center text-gray-500"
                >
                  No events available.
                </td>
              </tr>
            ) : (
              pagedEvents.map((event, idx) => {
                const d = parseEventDate(event.date);

                const dateStr = d
                  ? d.toLocaleDateString("en-IN")
                  : String(event.date ?? "");

                const c1d = event.change_1D;
                const c1w = event.change_1W;

                // Today / Upcoming / null (past event)
                const eventStatus = getEventStatus(
                  event.date
                );

                const statusClass =
                  eventStatus === "Today"
                    ? "text-blue-600 font-bold"
                    : "text-purple-600 font-bold";

                return (
                  <tr
                    key={
                      event.hash ??
                      `${event.symbol}-${event.date}-${idx}`
                    }
                    className={
                      idx % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50"
                    }
                  >
                    {/* Date */}
                    <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                      {dateStr}
                    </td>

                    {/* Purpose */}
                    <td className="px-4 py-2 border border-gray-200">
                      {event.purpose || "—"}
                    </td>

                    {/* 1D */}
                    <td
                      className={`px-4 py-2 border border-gray-200 tabular-nums ${
                        eventStatus
                          ? statusClass
                          : pctClass(c1d)
                      }`}
                    >
                      {eventStatus || pctText(c1d)}
                    </td>

                    {/* 1W */}
                    <td
                      className={`px-4 py-2 border border-gray-200 tabular-nums ${
                        eventStatus
                          ? statusClass
                          : pctClass(c1w)
                      }`}
                    >
                      {eventStatus || pctText(c1w)}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};


const StockCreditRatings = ({ data = [] }) => {
  const PAGE_SIZE = 5;

  const [sortField, setSortField] = useState("Date");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------

  const parseDate = (value) => {
    if (!value) return null;

    const match = String(value).match(
      /^(\d{4})-(\d{2})-(\d{2})/
    );

    if (match) {
      const [, y, m, d] = match;
      const date = new Date(+y, +m - 1, +d);

      return isNaN(date.getTime()) ? null : date;
    }

    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (value) => {
    const date = parseDate(value);

    return date
      ? date.toLocaleDateString("en-IN")
      : "—";
  };

  const formatAmount = (value) => {
    if (
      value == null ||
      String(value).trim() === "" ||
      isNaN(Number(value))
    ) {
      return "—";
    }

    return Number(value).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    });
  };

  const displayText = (value) =>
    value == null || String(value).trim() === ""
      ? "—"
      : String(value);

  // --------------------------------------------------
  // Sorting
  // --------------------------------------------------

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) =>
        d === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDir(
        ["Date", "Amount", "1D", "1W"].includes(field)
          ? "desc"
          : "asc"
      );
    }
  };

  const sortCaret = (field) =>
    sortField === field
      ? sortDir === "asc"
        ? "↑"
        : "↓"
      : "";

  const sortedRatings = useMemo(() => {
    const arr = [...(Array.isArray(data) ? data : [])];

    arr.sort((a, b) => {
      let av;
      let bv;

      switch (sortField) {
        case "Date":
          av =
            parseDate(a.dateOfCurrentCredit)?.getTime() ?? 0;
          bv =
            parseDate(b.dateOfCurrentCredit)?.getTime() ?? 0;
          break;

        case "Amount":
          av = Number(a.amount) || 0;
          bv = Number(b.amount) || 0;
          break;

        case "Rating":
          av = a.creditRating || "";
          bv = b.creditRating || "";
          break;

        case "Agency":
          av = a.creditAgencyName || "";
          bv = b.creditAgencyName || "";
          break;

        case "Action":
          av = a.currentAction || "";
          bv = b.currentAction || "";
          break;

        case "1D":
          av = parseFloat(a.change_1D);
          bv = parseFloat(b.change_1D);
          break;

        case "1W":
          av = parseFloat(a.change_1W);
          bv = parseFloat(b.change_1W);
          break;

        default:
          return 0;
      }

      // Missing numeric values always at bottom
      if (["1D", "1W"].includes(sortField)) {
        const aMissing = !Number.isFinite(av);
        const bMissing = !Number.isFinite(bv);

        if (aMissing && bMissing) return 0;
        if (aMissing) return 1;
        if (bMissing) return -1;
      }

      let result;

      if (
        typeof av === "number" &&
        typeof bv === "number"
      ) {
        result = av - bv;
      } else {
        result = String(av).localeCompare(
          String(bv),
          undefined,
          { sensitivity: "base" }
        );
      }

      return sortDir === "asc" ? result : -result;
    });

    return arr;
  }, [data, sortField, sortDir]);

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(sortedRatings.length / PAGE_SIZE)
  );

  const currentPage = Math.min(page, totalPages);

  const pagedRatings = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;

    return sortedRatings.slice(
      start,
      start + PAGE_SIZE
    );
  }, [sortedRatings, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [data, sortField, sortDir]);

  // --------------------------------------------------
  // Styling helpers
  // --------------------------------------------------

  const outlookClass = (value) => {
    const outlook = String(value || "").toLowerCase();

    if (outlook.includes("positive")) {
      return "text-green-600 font-medium";
    }

    if (outlook.includes("negative")) {
      return "text-red-600 font-medium";
    }

    if (outlook.includes("stable")) {
      return "text-blue-600 font-medium";
    }

    return "text-gray-700";
  };

  const actionClass = (value) => {
    const action = String(value || "").toLowerCase();

    if (
      action.includes("upgrade") ||
      action.includes("upward")
    ) {
      return "text-green-600 font-medium";
    }

    if (
      action.includes("downgrade") ||
      action.includes("downward")
    ) {
      return "text-red-600 font-medium";
    }

    return "text-gray-700";
  };

  const sortableHeader = (label, field) => (
    <th
      className="px-4 py-2 text-left border border-gray-200 cursor-pointer select-none whitespace-nowrap"
      onClick={() => toggleSort(field)}
      title={`Sort by ${label}`}
    >
      <span className="text-blue-600">
        {label} {sortCaret(field)}
      </span>
    </th>
  );

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div className="w-full mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-2">
        <h4 className="text-lg font-semibold">
          Credit Ratings
        </h4>

        {sortedRatings.length > PAGE_SIZE && (
          <div className="flex items-center gap-2 text-sm">
            <button
              onClick={() =>
                setPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Prev
            </button>

            <span className="text-gray-500 whitespace-nowrap">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() =>
                setPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded border disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm border-collapse">
          <thead className="bg-gray-100">
            <tr>
              {sortableHeader("Date", "Date")}

              {sortableHeader("Agency", "Agency")}

              <th className="px-4 py-2 text-left border border-gray-200">
                Facility
              </th>

              <th className="px-4 py-2 text-left border border-gray-200 whitespace-nowrap">
                Type
              </th>

              {sortableHeader("Rating", "Rating")}

              <th className="px-4 py-2 text-left border border-gray-200">
                Outlook
              </th>

              {sortableHeader("Amount (Cr.)", "Amount")}

              {sortableHeader("Action", "Action")}

              <th className="px-4 py-2 text-left border border-gray-200">
                Details
              </th>

              <th className="px-4 py-2 text-left border border-gray-200">
                Report
              </th>

            </tr>
          </thead>

          <tbody>
            {sortedRatings.length === 0 ? (
              <tr>
                <td
                  colSpan={10}
                  className="px-4 py-6 text-center text-gray-500"
                >
                  No credit ratings available.
                </td>
              </tr>
            ) : (
              pagedRatings.map((rating, idx) => (
                <tr
                  key={
                    rating.hash ??
                    `${rating.symbol}-${rating.dateOfCurrentCredit}-${idx}`
                  }
                  className={
                    idx % 2 === 0
                      ? "bg-white"
                      : "bg-gray-50"
                  }
                >
                  {/* Date */}
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {formatDate(
                      rating.dateOfCurrentCredit
                    )}
                  </td>

                  {/* Agency */}
                  <td className="px-4 py-2 border border-gray-200">
                    {displayText(
                      rating.creditAgencyName
                    )}
                  </td>

                  {/* Facility */}
                  <td className="px-4 py-2 border border-gray-200">
                    {displayText(
                      rating.ratingAssigned
                    )}
                  </td>

                  {/* Type */}
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {displayText(
                      rating.typeOfRating
                    )}
                  </td>

                  {/* Rating */}
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap font-semibold text-blue-700">
                    {displayText(
                      rating.creditRating
                    )}
                  </td>

                  {/* Outlook */}
                  <td
                    className={`px-4 py-2 border border-gray-200 whitespace-nowrap ${outlookClass(
                      rating.outlook
                    )}`}
                  >
                    {displayText(rating.outlook)}
                  </td>

                  {/* Amount */}
                  <td className="px-4 py-2 border border-gray-200 text-right tabular-nums whitespace-nowrap">
                    {formatAmount(rating.amount)}
                  </td>

                  {/* Action */}
                  <td
                    className={`px-4 py-2 border border-gray-200 ${actionClass(
                      rating.currentAction
                    )}`}
                  >
                    {displayText(
                      rating.currentAction
                    )}
                  </td>

                  {/* Brief details */}
                  <td className="px-4 py-2 border border-gray-200">
                    {displayText(
                      rating.brieFDetails
                    )}
                  </td>

                  {/* Report link */}
                  <td className="px-4 py-2 border border-gray-200 whitespace-nowrap">
                    {/^https?:\/\//i.test(
                      String(rating.detailsOfRatingLink || "")
                    ) ? (
                      <a
                        href={rating.detailsOfRatingLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        View ↗
                      </a>
                    ) : (
                      <span className="text-gray-400">
                        —
                      </span>
                    )}
                  </td>

                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StockBrokerageReports = ({ data = [] }) => {
  const PAGE_SIZE = 5;

  const [activeTab, setActiveTab] = useState("All");
  const [sortField, setSortField] = useState("RECOMMEND DATE");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);

  // --------------------------------------------------
  // Helpers
  // --------------------------------------------------

  const toNumber = (value) => {
    if (
      value === null ||
      value === undefined ||
      String(value).trim() === ""
    ) {
      return null;
    }

    const number = Number(
      String(value).replace(/,/g, "").replace(/%/g, "").trim()
    );

    return Number.isFinite(number) ? number : null;
  };

  const parseDate = (value) => {
    if (!value || value === "########") return null;

    const date = new Date(value);

    return Number.isNaN(date.getTime()) ? null : date;
  };

  const formatDate = (value) => {
    const date = parseDate(value);

    return date
      ? date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "—";
  };

  const formatPrice = (value) => {
    const number = toNumber(value);

    return number === null
      ? "—"
      : number.toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        });
  };

  const formatPercent = (value) => {
    const number = toNumber(value);

    if (number === null) return "—";

    return (
      <span
        className={
          number > 0
            ? "text-green-600 font-medium"
            : number < 0
            ? "text-red-600 font-medium"
            : "text-gray-600"
        }
      >
        {number > 0 ? "+" : ""}
        {number.toFixed(2)}%
      </span>
    );
  };

  const displayText = (value) =>
    value == null || String(value).trim() === ""
      ? "—"
      : String(value);

  // --------------------------------------------------
  // Action Helpers
  // --------------------------------------------------

  const getActionCategory = (value) => {
    const action = String(value || "").trim().toLowerCase();

    if (/^(buy|outperform|overweight)/.test(action)) return "Buy";

    if (/^(sell|reduce|underperform|underweight)/.test(action))
      return "Sell";

    if (/^(hold|neutral|market perform)/.test(action))
      return "Hold";

    if (/^(accumulate|add)/.test(action))
      return "Accumulate";

    return "Other";
  };

  const ACTION_TABS = [
    "All",
    "Buy",
    "Sell",
    "Hold",
    "Accumulate",
    "Other",
  ];

  const getTabColor = (tab, selected) => {
    const colors = {
      All: {
        normal: "bg-gray-100 text-gray-700 border-gray-300",
        active: "bg-gray-700 text-white border-gray-700",
      },
      Buy: {
        normal: "bg-green-50 text-green-700 border-green-200",
        active: "bg-green-600 text-white border-green-600",
      },
      Sell: {
        normal: "bg-red-50 text-red-700 border-red-200",
        active: "bg-red-600 text-white border-red-600",
      },
      Hold: {
        normal: "bg-blue-50 text-blue-700 border-blue-200",
        active: "bg-blue-600 text-white border-blue-600",
      },
      Accumulate: {
        normal: "bg-yellow-50 text-yellow-700 border-yellow-200",
        active: "bg-yellow-500 text-white border-yellow-500",
      },
      Other: {
        normal: "bg-purple-50 text-purple-700 border-purple-200",
        active: "bg-purple-600 text-white border-purple-600",
      },
    };

    const color = colors[tab] || colors.Other;

    return selected ? color.active : color.normal;
  };

  const getActionColor = (value) => {
    const action = getActionCategory(value);

    switch (action) {
      case "Buy":
        return "bg-green-100 text-green-700";

      case "Sell":
        return "bg-red-100 text-red-700";

      case "Hold":
        return "bg-blue-100 text-blue-700";

      case "Accumulate":
        return "bg-yellow-100 text-yellow-700";

      default:
        return "bg-gray-100 text-gray-600";
    }
  };

  // --------------------------------------------------
  // Action Counts
  // --------------------------------------------------

  const actionCounts = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];

    const counts = {
      All: rows.length,
      Buy: 0,
      Sell: 0,
      Hold: 0,
      Accumulate: 0,
      Other: 0,
    };

    rows.forEach((row) => {
      const category = getActionCategory(
        row["ACTION"] || row["CURRENT RECOMMENDATION"]
      );

      counts[category] += 1;
    });

    return counts;
  }, [data]);

  // --------------------------------------------------
  // Sorting
  // --------------------------------------------------

  const toggleSort = (field) => {
    if (sortField === field) {
      setSortDir((prev) =>
        prev === "asc" ? "desc" : "asc"
      );
    } else {
      setSortField(field);
      setSortDir("desc");
    }

    setPage(1);
  };

  const sortCaret = (field) =>
    sortField === field
      ? sortDir === "asc"
        ? "↑"
        : "↓"
      : "↕";

  // --------------------------------------------------
  // Filter + Sort
  // --------------------------------------------------

  const filteredReports = useMemo(() => {
    let rows = Array.isArray(data) ? [...data] : [];

    // Action filter
    if (activeTab !== "All") {
      rows = rows.filter(
        (row) =>
          getActionCategory(
            row["ACTION"] || row["CURRENT RECOMMENDATION"]
          ) === activeTab
      );
    }

    // Sorting
    rows.sort((a, b) => {
      let av;
      let bv;

      if (sortField === "RECOMMEND DATE") {
        av = parseDate(a[sortField])?.getTime() ?? null;
        bv = parseDate(b[sortField])?.getTime() ?? null;
      } else if (
        [
          "TARGET PRICE",
          "RECOMMENDED PRICE",
          "CURRENT PRICE",
          "CURRENT RETURNS (%)",
          "POTENTIAL RETURNS (%)",
          "PREVIOUS TARGET PRICE",
        ].includes(sortField)
      ) {
        av = toNumber(a[sortField]);
        bv = toNumber(b[sortField]);
      } else {
        av = String(a[sortField] || "");
        bv = String(b[sortField] || "");
      }

      // Missing values at bottom
      const aMissing = av === null || av === "";
      const bMissing = bv === null || bv === "";

      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;

      let result;

      if (
        typeof av === "number" &&
        typeof bv === "number"
      ) {
        result = av - bv;
      } else {
        result = String(av).localeCompare(
          String(bv),
          undefined,
          { sensitivity: "base" }
        );
      }

      return sortDir === "asc" ? result : -result;
    });

    return rows;
  }, [data, activeTab, sortField, sortDir]);

  // --------------------------------------------------
  // Pagination
  // --------------------------------------------------

  const totalPages = Math.max(
    1,
    Math.ceil(filteredReports.length / PAGE_SIZE)
  );

  const currentPage = Math.min(page, totalPages);

  const pagedReports = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;

    return filteredReports.slice(
      start,
      start + PAGE_SIZE
    );
  }, [filteredReports, currentPage]);

  useEffect(() => {
    setPage(1);
  }, [data, activeTab]);

  // --------------------------------------------------
  // Sortable Header
  // --------------------------------------------------

  const sortableHeader = (label, field) => (
    <th
      className="px-3 py-2 text-left border border-gray-200 cursor-pointer select-none whitespace-nowrap"
      onClick={() => toggleSort(field)}
      title={`Sort by ${label}`}
    >
      <span className="text-blue-600">
        {label} {sortCaret(field)}
      </span>
    </th>
  );

  // --------------------------------------------------
  // Render
  // --------------------------------------------------

  return (
    <div className="w-full mt-6">

      {/* Header */}

      <div className="flex items-center justify-between mb-3">

        <h4 className="text-lg font-semibold">
          Brokerage Reports
        </h4>

        {filteredReports.length > PAGE_SIZE && (
          <div className="flex items-center gap-2 text-sm">

            <button
              onClick={() =>
                setPage((p) => Math.max(1, p - 1))
              }
              disabled={currentPage === 1}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >
              Prev
            </button>

            <span className="text-gray-500 whitespace-nowrap">
              {currentPage} / {totalPages}
            </span>

            <button
              onClick={() =>
                setPage((p) =>
                  Math.min(totalPages, p + 1)
                )
              }
              disabled={currentPage === totalPages}
              className="px-2 py-1 rounded border disabled:opacity-40"
            >
              Next
            </button>

          </div>
        )}

      </div>

      {/* Action Tabs */}

      <div className="flex gap-2 overflow-x-auto pb-3 mb-3">

        {ACTION_TABS.map((tab) => {
          const selected = activeTab === tab;

          return (
            <button
              key={tab}
              onClick={() => {
                setActiveTab(tab);
                setPage(1);
              }}
              className={`
                flex items-center gap-2
                flex-shrink-0
                px-3 py-1.5
                rounded-md
                text-xs font-semibold
                border
                transition-all duration-150
                hover:shadow-sm
                ${getTabColor(tab, selected)}
              `}
            >
              {tab}

              <span
                className={`
                  px-1.5 py-0.5
                  rounded-full
                  text-[10px] font-bold
                  ${
                    selected
                      ? "bg-white bg-opacity-20 text-white"
                      : "bg-white bg-opacity-70"
                  }
                `}
              >
                {actionCounts[tab] || 0}
              </span>

            </button>
          );
        })}

      </div>

      {/* Table */}

      <div className="overflow-x-auto rounded-xl border">

        <table className="min-w-full text-sm border-collapse whitespace-nowrap">

          <thead className="bg-gray-100">

            <tr>

              {sortableHeader("Date", "RECOMMEND DATE")}

              {sortableHeader("Brokerage", "BROKERAGE NAME")}

              {sortableHeader("Action", "ACTION")}

              {sortableHeader("Target", "TARGET PRICE")}

              {sortableHeader("Recommended", "RECOMMENDED PRICE")}

              {sortableHeader("Current", "CURRENT PRICE")}

              {sortableHeader("Return %", "CURRENT RETURNS (%)")}

              {sortableHeader("Potential %", "POTENTIAL RETURNS (%)")}

              {sortableHeader(
                "Previous Action",
                "PREVIOUS RECOMMENDATION"
              )}

              {sortableHeader(
                "Previous Target",
                "PREVIOUS TARGET PRICE"
              )}

              <th className="px-3 py-2 text-left border border-gray-200">
                Report
              </th>

            </tr>

          </thead>

          <tbody>

            {pagedReports.length === 0 ? (

              <tr>
                <td
                  colSpan={11}
                  className="px-4 py-6 text-center text-gray-500"
                >
                  No brokerage reports available.
                </td>
              </tr>

            ) : (

              pagedReports.map((row, index) => {
                const pdfUrl = String(
                  row["PDF URL"] || ""
                ).trim();

                return (
                  <tr
                    key={
                      row["ID"] ||
                      `${row["BROKERAGE NAME"]}-${row["RECOMMEND DATE"]}-${index}`
                    }
                    className={
                      index % 2 === 0
                        ? "bg-white"
                        : "bg-gray-50"
                    }
                  >

                    {/* Date */}

                    <td className="px-3 py-2 border border-gray-200">
                      {formatDate(row["RECOMMEND DATE"])}
                    </td>

                    {/* Brokerage */}

                    <td className="px-3 py-2 border border-gray-200 font-medium">
                      {displayText(row["BROKERAGE NAME"])}
                    </td>

                    {/* Action */}

                    <td className="px-3 py-2 border border-gray-200">

                      <span
                        className={`px-2 py-1 rounded text-xs font-semibold ${getActionColor(
                          row["ACTION"] ||
                          row["CURRENT RECOMMENDATION"]
                        )}`}
                      >
                        {displayText(
                          row["ACTION"] ||
                          row["CURRENT RECOMMENDATION"]
                        )}
                      </span>

                    </td>

                    {/* Target Price */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPrice(row["TARGET PRICE"])}
                    </td>

                    {/* Recommended Price */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPrice(row["RECOMMENDED PRICE"])}
                    </td>

                    {/* Current Price */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPrice(row["CURRENT PRICE"])}
                    </td>

                    {/* Current Returns */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPercent(
                        row["CURRENT RETURNS (%)"]
                      )}
                    </td>

                    {/* Potential Returns */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPercent(
                        row["POTENTIAL RETURNS (%)"]
                      )}
                    </td>

                    {/* Previous Action */}

                    <td className="px-3 py-2 border border-gray-200">

                      <span
                        className={`px-2 py-1 rounded text-xs font-medium ${getActionColor(
                          row["PREVIOUS RECOMMENDATION"]
                        )}`}
                      >
                        {displayText(
                          row["PREVIOUS RECOMMENDATION"]
                        )}
                      </span>

                    </td>

                    {/* Previous Target */}

                    <td className="px-3 py-2 border border-gray-200 text-right tabular-nums">
                      {formatPrice(
                        row["PREVIOUS TARGET PRICE"]
                      )}
                    </td>

                    {/* PDF */}

                    <td className="px-3 py-2 border border-gray-200">

                      {/^https?:\/\//i.test(pdfUrl) ? (
                        <a
                          href={pdfUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:underline font-medium"
                        >
                          View PDF ↗
                        </a>
                      ) : (
                        "—"
                      )}

                    </td>

                  </tr>
                );
              })

            )}

          </tbody>

        </table>

      </div>

    </div>
  );
};

const StockAnnouncements = ({ data = [] }) => {
  // ✅ Hooks always first; no early returns before hooks
  const [page, setPage] = useState(1);
  const [sortField, setSortField] = useState(null); // "1D" | "1W" | null
  const [sortDir, setSortDir] = useState("desc");   // "asc" | "desc"

  // Safe data
  const rows = Array.isArray(data) ? data : [];

  // Helpers (no hooks)
  const siteName = (url) => {
    if (!url) return "link";
    try {
      const host = new URL(url).hostname.replace(/^www\./i, "");
      return host.split(".")[0] || host;
    } catch {
      return "link";
    }
  };

  // "25 Sept 2025"
  const formatPrettyDate = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return String(d || "");
    return dt.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const toNum = (v) => {
    if (v == null) return null;          // null / undefined
    if (typeof v === "string" && v.trim() === "") return null; // ""
    const n = Number(v);
    return Number.isFinite(n) ? n : null; // NaN / Infinity -> null
  };

  const pctText = (v) =>
    v == null || isNaN(v) ? "" : `${Number(v).toFixed(2)}%`;
  const pctClass = (v) =>
    v == null || isNaN(v) ? "text-gray-500" : Number(v) >= 0 ? "text-green-600" : "text-red-600";

  const toggleSort = (field) => {
    if (sortField === field) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortField(field);
      setSortDir("desc");
    }
    setPage(1);
  };
  const caret = (field) => (sortField === field ? (sortDir === "asc" ? "↑" : "↓") : "");

  // Sort all, then paginate
  const sorted = useMemo(() => {
    const arr = [...rows];

    const cmpMissingLast = (av, bv) => {
      const aMissing = av == null;
      const bMissing = bv == null;
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;   // a goes AFTER b
      if (bMissing) return -1;  // b goes AFTER a
      return sortDir === "asc" ? av - bv : bv - av;
    };

    if (sortField === "1D") {
      arr.sort((a, b) => {
        const av = toNum(a?.change_1D ?? a?.chang);
        const bv = toNum(b?.change_1D ?? b?.chang);
        return cmpMissingLast(av, bv);
      });
    } else if (sortField === "1W") {
      arr.sort((a, b) => {
        const av = toNum(a?.change_1W);
        const bv = toNum(b?.change_1W);
        return cmpMissingLast(av, bv);
      });
    } else {
      arr.sort((a, b) => {
        const ta = new Date(a?.an_dt || a?.date || 0).getTime();
        const tb = new Date(b?.an_dt || b?.date || 0).getTime();
        return (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
      });
    }

    return arr;
  }, [rows, sortField, sortDir]);


  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const pageSafe = Math.min(Math.max(1, page), totalPages);
  const start = (pageSafe - 1) * PAGE_SIZE;
  const pageSlice = sorted.slice(start, start + PAGE_SIZE);

  const goPrev = () => setPage((p) => Math.max(1, p - 1));
  const goNext = () => setPage((p) => Math.min(totalPages, p + 1));

  return (
    <div className="w-full mt-6">
      <h4 className="text-lg font-semibold">Announcements</h4>

      {/* Top controls (paging + sort) */}
      <div className="flex flex-wrap items-center justify-between gap-2 mt-2 mb-2">
        <div className="text-sm text-gray-500">
          Showing {pageSlice.length} of {sorted.length} items
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => toggleSort("1D")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
            title="Sort by 1D change"
          >
            1D% {caret("1D")}
          </button>
          <button
            onClick={() => toggleSort("1W")}
            className="px-1.5 py-1.5 rounded-md border text-sm"
            title="Sort by 1W change"
          >
            1W% {caret("1W")}
          </button>
          <button
            onClick={goPrev}
            disabled={pageSafe === 1}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Prev
          </button>
          <span className="text-sm tabular-nums">
            Page {pageSafe} of {totalPages}
          </span>
          <button
            onClick={goNext}
            disabled={pageSafe === totalPages}
            className="px-3 py-1.5 rounded-md border text-sm disabled:opacity-50"
          >
            Next
          </button>
        </div>
      </div>

      {/* List-style rows in a bordered, rounded table for alignment/scroll */}
      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <tbody className="divide-y">
            {pageSlice.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500">No announcements found.</td>
              </tr>
            ) : (
              pageSlice.map((row, idx) => {
                const type = row?.desc || row?.purpose || "—";
                const description = row?.attchmntText || row?.bm_desc || "";
                const href = row?.attchmntFile || row?.link || null;
                const dateRaw = row?.an_dt || row?.date || null;
                const dateStr = formatPrettyDate(dateRaw);

                const c1d = parseFloat(row?.change_1D ?? row?.chang ?? null);
                const c1w = parseFloat(row?.change_1W ?? null);

                return (
                  <tr
                    key={row.hash ?? `${row.symbol}-${dateRaw}-${idx}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-4 py-3 border">
                      {/* {Type} – {Description} – {link} – {date} – (1D …) (1W …) */}
                      <div className="space-x-1">
                        <span className="font-medium">{type}</span>
                        {description && <span className="font-light text-gray-500">– {description}</span>}
                        {href && (
                          <>
                            <span>–</span>
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-blue-600 underline"
                              title={href}
                            >
                              {siteName(href)}
                            </a>
                          </>
                        )}
                        {dateStr && (
                          <>
                            <span>–</span>
                            <span className="text-gray-700">{dateStr}</span>
                          </>
                        )}
                        {Number.isFinite(c1d) && (
                          <span className={`ml-2 ${pctClass(c1d)}`}>{`1D ${pctText(c1d)}`}</span>
                        )}
                        {Number.isFinite(c1w) && (
                          <span className={`ml-2 ${pctClass(c1w)}`}>{`1W ${pctText(c1w)}`}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Empty state AFTER hooks—no early return */}
      {rows.length === 0 && (
        <p className="mt-3 text-gray-500">No announcements available.</p>
      )}
    </div>
  );
};




const StockHeader = ({
  stockInfo,
  symbol,
  tickPrefix = "₹",
  decimalPoints = 2,
}) => {
  const { isMobile } = useDevice();
  if (!stockInfo) return null;

  const renderPriceChange = () => {
    if (!stockInfo.currentPrice || !stockInfo.previousClose) return null;

    const curr = parseFloat(stockInfo.currentPrice);
    const prev = parseFloat(stockInfo.previousClose);

    if (isNaN(curr) || isNaN(prev) || prev === 0) return null;

    const percentChange = ((curr - prev) / prev) * 100;
    const isPositive = percentChange >= 0;
    const changeColor = isPositive ? "text-green-600" : "text-red-600";

    return (
      <span className={`text-sm font-semibold ${changeColor}`}>
        {`${isPositive ? "+" : ""}${percentChange.toFixed(2)}%`}
      </span>
    );
  };

  const renderSummary = () => {
    const summary = stockInfo.longBusinessSummary;
    if (!summary) return null;

    const first300 = summary.slice(0, 300);
    const lastPeriodIdx = first300.lastIndexOf(".");

    if (lastPeriodIdx !== -1) {
      return first300.slice(0, lastPeriodIdx + 1);
    }

    const firstSentenceEnd = summary.indexOf(".");
    return firstSentenceEnd !== -1
      ? summary.slice(0, firstSentenceEnd + 1)
      : summary;
  };

  const isValidNumber = (v) => {
    const n = parseFloat(v);
    return Number.isFinite(n) && n !== 0;
  };

  const fmtNum = (v, digits = 2) => {
    const n = parseFloat(v);
    return Number.isFinite(n) ? n.toFixed(digits) : "—";
  };

  const StockMetric = ({ label, value }) => (
    <div className="flex items-center justify-between gap-3 px-3 py-2.5 min-w-0">
      <span className="text-gray-500 text-xs sm:text-sm whitespace-nowrap">
        {label}
      </span>

      <span className="text-gray-900 text-sm font-semibold text-right tabular-nums break-words">
        {value ?? "—"}
      </span>
    </div>
  );

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:justify-between gap-4 mb-4">
        {/* Left Column — 1/4 width */}
        <div className="flex-[1]">
          {/* Row 1: Name, Price, % Change */}
          <div className="flex flex-col">
            <h3 className="text-2xl font-bold">
            { stockInfo.quoteType === "SECTOR" ? formatIndustryName(stockInfo.longName)  : stockInfo.longName}
            </h3>
            <div className="text-base text-gray-500">{symbol}</div>
          </div>

          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1 mt-1">
            {/* Price */}
            {(() => {
              const priceNum = parseFloat(stockInfo.currentPrice);
              if (isNaN(priceNum)) return <span className="text-base">—</span>;

              return (
                <span className="text-base font-medium">
                  {tickPrefix} {priceNum.toFixed(decimalPoints)}
                </span>
              );
            })()}

            {/* % Change */}
            {renderPriceChange()}

            {/* Last update date */}
            {stockInfo.lastUpdateDate && (
              <span className="text-gray-500 text-xs">
                {stockInfo.quoteType === "EQUITY" ? formatDateTime(stockInfo.lastUpdateDate) : formatDate(stockInfo.lastUpdateDate)}
              </span>
            )}

            {/* <span className="leading-tight">
              <span className="text-gray-500 text-xs sm:text-sm">NSE:</span>{" "}
            </span> */}
          </div>

          {/* Stock Fundamentals & Trading Metrics */}
          {["EQUITY", "GLOBAL_INDEX", "GLOBAL_STOCKS"].includes(stockInfo.quoteType) && (

            <div className="w-full md:w-fit max-w-full my-3 rounded-lg border border-gray-200 bg-gray-50/50 overflow-hidden">

              {/* Row 1: Fundamentals */}
              {["EQUITY", "GLOBAL_STOCKS"].includes(stockInfo.quoteType) && (

                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 border-b border-gray-200">

                  <StockMetric
                    label="M Cap"
                    value={(() => {

                      if (stockInfo.quoteType === "EQUITY") {
                        const mcap = parseFloat(stockInfo.marketCap);

                        if (!(mcap > 0)) return "—";

                        return `₹ ${new Intl.NumberFormat("en-IN", {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        }).format(mcap / 1e7)} Cr`;
                      }

                      const mcap = parseFloat(stockInfo.nonDilutedMarketCap);

                      if (!(mcap > 0)) return "—";

                      return `${getCurrencySymbol(stockInfo.currency)} ${new Intl.NumberFormat("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      }).format(mcap / 1e9)} B`;

                    })()}
                  />

                  <StockMetric
                    label="PE"
                    value={
                      isNaN(parseFloat(stockInfo.trailingPE))
                        ? "—"
                        : parseFloat(stockInfo.trailingPE).toFixed(1)
                    }
                  />

                  <StockMetric
                    label="ROE"
                    value={
                      stockInfo.returnOnEquity != null &&
                      !isNaN(Number(stockInfo.returnOnEquity))
                        ? `${(Number(stockInfo.returnOnEquity) * 100).toFixed(2)}%`
                        : "—"
                    }
                  />

                </div>
              )}

              {/* Row 2: Trading Metrics */}
              {["EQUITY", "GLOBAL_INDEX"].includes(stockInfo.quoteType) &&
                isValidNumber(stockInfo.PCR_OI) && (

                  <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200 border-b border-gray-200">

                    <StockMetric
                      label="Op PCR"
                      value={fmtNum(stockInfo.PCR_OI, 2)}
                    />

                    <StockMetric
                      label="Fut OI"
                      value={
                        isValidNumber(stockInfo.OI)
                          ? `${fmtNum(stockInfo.OI, 2)}%`
                          : "—"
                      }
                    />

                    <StockMetric
                      label="Del"
                      value={
                        isValidNumber(stockInfo.Delivery_pc)
                          ? `${fmtNum(stockInfo.Delivery_pc, 2)}%`
                          : "—"
                      }
                    />

                  </div>
                )}

              {/* Row 3: Risk & Margin Metrics */}
              {stockInfo.quoteType === "EQUITY" && (

                <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-200">

                  <StockMetric
                    label="Band"
                    value={
                      stockInfo.PriceBand != null &&
                      String(stockInfo.PriceBand).trim() !== ""
                        ? isNaN(Number(stockInfo.PriceBand))
                          ? stockInfo.PriceBand
                          : `${stockInfo.PriceBand}%`
                        : "—"
                    }
                  />

                  <StockMetric
                    label="Haircut"
                    value={
                      stockInfo.Haircut != null &&
                      String(stockInfo.Haircut).trim() !== "" &&
                      !isNaN(Number(stockInfo.Haircut))
                        ? `${Number(stockInfo.Haircut).toFixed(1)}%`
                        : "—"
                    }
                  />

                  <StockMetric
                    label="Margin%"
                    value={
                      Number(stockInfo.marketCap) > 0 &&
                      stockInfo.Margin_Val != null &&
                      String(stockInfo.Margin_Val).trim() !== "" &&
                      !isNaN(Number(stockInfo.Margin_Val))
                        ? `${(
                            (Number(stockInfo.Margin_Val) /
                              Number(stockInfo.marketCap)) *
                            100
                          ).toFixed(3)}%`
                        : "—"
                    }
                  />

                </div>
              )}

            </div>
          )}

          {/* Row 4: Website Link */}
          {["EQUITY", "GLOBAL_STOCKS", "INDIA_INDEX"].includes(stockInfo.quoteType) && (
            <div className="text-base text-gray-800 flex flex-wrap gap-3 mb-1">

              {/* Website link (EQUITY + GLOBAL_STOCKS) */}
              {stockInfo.website && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">🔗</span>
                  <a
                    href={
                      stockInfo.website.startsWith("http")
                        ? stockInfo.website
                        : `https://${stockInfo.website}`
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    {stockInfo.quoteType === "INDIA_INDEX"
                      ? "www.nseindia.com"
                      : stockInfo.website.replace(/^https?:\/\//, "")}
                  </a>
                </div>
              )}

              {/* Screener link (ONLY EQUITY) */}
              {stockInfo.quoteType === "EQUITY" && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">🔗</span>
                  <a
                    href={`https://www.screener.in/company/${symbol}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    screener.in
                  </a>
                </div>
              )}

              {stockInfo.quoteType === "GLOBAL_STOCKS" && (
                <div className="flex items-center gap-1">
                  <span className="text-gray-600">🔗</span>
                  <a
                    href={`https://finance.yahoo.com/quote/${symbol}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 underline break-all"
                  >
                    Yahoo Finance
                  </a>
                </div>
              )}

            </div>
          )}
        </div>

        {/* Right Column — 3/4 width */}
        {stockInfo.longBusinessSummary && (
          <div className="flex-[1]">
            <div className="mb-1 flex items-baseline">
              <h4 className="text-base font-semibold">About</h4>
            </div>

            <div>
              {!["N", "NEW"].includes(stockInfo.tjiIndustry?.trim().toUpperCase()) && (
                <span className="text-sm text-gray-500 font-normal">
                  Sector:{" "}
                  {stockInfo.tjiIndustry
                    ?.split("/")
                    .map((rawIndustry, idx, arr) => {
                      const industryLabel = rawIndustry.replace(
                        /([a-z])([A-Z])/g,
                        "$1 $2"
                      );

                      const industryKey = rawIndustry
                        .toUpperCase()
                        .replace(/([a-z])([A-Z])/g, "$1_$2");

                      return (
                        <span key={rawIndustry} className="inline-flex items-center gap-1">
                          {stockInfo.quoteType === "EQUITY" ?
                          <a
                            href={`/symbol/${encodeURIComponent(industryKey)}_SECTOR`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700"
                          >
                            <span>{industryLabel}</span>
                          </a>
                          :
                          <span className="text-blue-600">{industryLabel}</span>
                          }

                          {idx < arr.length - 1 && <span className="mx-1">/</span>}
                        </span>
                      );
                    })}
                </span>
              )}

              {(stockInfo.quoteType === "GLOBAL_STOCKS" ||
                stockInfo.quoteType === "EQUITY") &&
                (stockInfo.city?.trim() || stockInfo.country?.trim()) && (
                  <div className="text-sm text-gray-500 font-normal mt-1">
                    Headquarters:{" "}

                    {stockInfo.city?.trim() && (
                      <a
                        href={`/search/city/${encodeURIComponent(stockInfo.city.trim())}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {stockInfo.city.trim()}
                      </a>
                    )}

                    {stockInfo.city?.trim() && stockInfo.country?.trim() && (
                      <span className="text-gray-700">, </span>
                    )}

                    {stockInfo.country?.trim() && (
                      <span className="text-gray-700">
                        {stockInfo.country.trim()}
                      </span>
                    )}
                  </div>
              )}


              {/* Holding Group */}
              {stockInfo.holdingGroup?.trim() && (
                <div className="text-sm text-gray-500 font-normal mt-1">
                  Major Holding:{" "}

                  <a
                    href={`/search/holding/${encodeURIComponent(
                      stockInfo.holdingGroup.trim()
                    )}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:underline"
                  >
                    {stockInfo.holdingGroup
                      .replace(/\s*\/\s*/g, " / ")
                      .replace(/([a-z])([A-Z])/g, "$1 $2")}
                  </a>
                </div>
              )}
      
            </div>
            
            <p className="text-sm text-gray-500 leading-relaxed mt-1">
              {renderSummary()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

const PCT_FIELDS = ["vs52WH", "1D", "1W", "1M", "3M", "6M", "1Y"];

const toPctNum = (v) => {
  // Accepts "3.67%", "-2.1%", "N/A", null, undefined
  if (v == null) return null;
  const s = String(v).trim();
  if (!s || s.toUpperCase() === "N/A" || s === "-") return null;
  const n = Number(s.replace("%", ""));
  return Number.isFinite(n) ? n : null;
};

const pctClass = (n) => {
  if (n == null) return "text-gray-500";
  return n >= 0 ? "text-green-600" : "text-red-600";
};

const pctText = (n, original) => {
  if (n == null) return original && String(original).trim() ? String(original) : "—";
  return `${n.toFixed(2)}%`;
};

const SectorInfoTable = ({ sectorResp, isMobile }) => {
  // sectorResp = { ok:true, key:"MedicalEquipment", data:{ stocks:[...], ... } }

  const [sortConfig, setSortConfig] = useState({ key: "1D", dir: "desc" });

  const rows = Array.isArray(sectorResp?.data?.stocks) ? sectorResp.data.stocks : [];

  // mobile gets tighter "#"
  const COL_W = { idx: isMobile ? 25 : 50, trend: isMobile ? 50 : 70  };
  const LEFT_TREND = COL_W.idx;
  const LEFT_NAME = COL_W.idx + COL_W.trend;

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
      // default directions
      const defaultDir = key === "name" ? "asc" : "desc";
      return { key, dir: defaultDir };
    });
  };

  const caret = (key) => (sortConfig.key === key ? (sortConfig.dir === "asc" ? "↑" : "↓") : "");

  const sortedRows = useMemo(() => {
    const arr = [...rows];
    const { key, dir } = sortConfig;
    const sign = dir === "asc" ? 1 : -1;

    // Missing always last
    const missingLast = (aMissing, bMissing) => {
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return null;
    };

    arr.sort((a, b) => {
      if (key === "name") {
        const av = (a?.name || a?.symbol || "").toString().trim();
        const bv = (b?.name || b?.symbol || "").toString().trim();
        const aMissing = !av;
        const bMissing = !bv;

        const miss = missingLast(aMissing, bMissing);
        if (miss != null) return miss;

        return sign * av.localeCompare(bv, "en", { sensitivity: "base" });
      }

      // % fields live in dummyData
      const av = toPctNum(a?.dummyData?.[key]);
      const bv = toPctNum(b?.dummyData?.[key]);

      const miss = missingLast(av == null, bv == null);
      if (miss != null) return miss;

      return sign * (av - bv);
    });

    return arr;
  }, [rows, sortConfig]);

  return (
    <div className="w-full mt-4">
      <div className="flex items-baseline justify-between gap-3 mb-2">
        <h3 className="text-lg font-semibold">
          {formatIndustryName(sectorResp?.key)}{" "}
          <span className="text-sm font-normal text-gray-500">
            ({sortedRows.length} stocks)
          </span>
        </h3>
      </div>

      <div className="overflow-x-auto rounded-xl border">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-gray-700">
              <th className="sticky left-0 top-0 z-40 bg-gray-50 px-3 py-2 text-left"
                  style={{ width: COL_W.idx, minWidth: COL_W.idx }}>
                #
              </th>

              <th className="sticky top-0 z-40 bg-gray-50 p-2 text-left"
                  style={{ left: LEFT_TREND, width: COL_W.trend, minWidth: COL_W.trend, maxWidth: COL_W.trend }}>
                Trend
              </th>

              <th
                className={`sticky top-0 z-40 bg-gray-50 p-2 text-left cursor-pointer select-none
                  ${isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[250px] max-w-[250px]"}
                `}
                style={{ left: LEFT_NAME }}
                onClick={() => toggleSort("name")}
              >
                Name {caret("name")}
              </th>

              {PCT_FIELDS.map((f) => (
                <th
                  key={f}
                  className="sticky top-0 z-20 bg-gray-50 px-3 py-2 text-right cursor-pointer select-none whitespace-nowrap"
                  onClick={() => toggleSort(f)}
                  title={`Sort by ${f}`}
                >
                  {f} {caret(f)}
                </th>
              ))}
            </tr>
          </thead>


          <tbody className="divide-y">
            {sortedRows.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-center text-gray-500" colSpan={3 + PCT_FIELDS.length}>
                  No data.
                </td>
              </tr>
            ) : (
              sortedRows.map((r, idx) => {
                const nm = r?.name || r?.symbol || "—";
                const sym = r?.symbol || "";

                return (
                  <tr key={sym || idx} className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}>
                    <td
                      className="sticky left-0 z-30 bg-blue-50 px-3 py-2 text-left tabular-nums text-blue-600"
                      style={{ width: COL_W.idx, minWidth: COL_W.idx }}
                    >
                      {idx + 1}
                    </td>

                    <td
                      className="sticky z-30 bg-blue-50 p-2 text-blue-600"
                      style={{ left: LEFT_TREND, width: COL_W.trend, minWidth: COL_W.trend, maxWidth: COL_W.trend }}
                    >
                      <Sparklines data={r?.sparklineData || []} height={20} width={100}>
                        <SparklinesLine color="blue" style={{ strokeWidth: 2, fill: "none" }} />
                      </Sparklines>
                    </td>

                    <td
                      className={`sticky bg-blue-50 z-30 p-2 text-blue-400 ${
                        isMobile ? "min-w-[200px] max-w-[200px]" : "min-w-[200px] max-w-[200px]"
                      }`}
                      style={{ left: LEFT_NAME }}
                    >
                      <a
                        href={`/symbol/${r.symbol}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 underline"
                      >
                        {r.name}
                      </a>
                        {" "} 
                        {
                        `(CMP ₹${(r.price ?? 0).toFixed(2)})`
                        }                             
                        <>
                        <br />
                          {"PE " + (r.pe ?? 0).toFixed(2) +
                          " | ROE " + ((r.roe ?? 0) * 100).toFixed(2) + "%" +
                          " | Mcap " +  `₹${new Intl.NumberFormat("en-IN", {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2,
                                          }).format(parseFloat(r.marketCap) / 1e7)} Cr`}
                        <br />
                        {Array.isArray(r.events) && r.events.length > 0 && (
                          <div className="text-gray-500 text-sm mt-1">
                            {r.events.map((e, idx) => (
                              <div key={idx}>📌 {e.purpose} - {formatDate(e.date)}</div>
                            ))}
                          </div>
                        )}
                          </>
                    </td>

                    {PCT_FIELDS.map((f) => {
                      const raw = r?.dummyData?.[f];
                      const n = toPctNum(raw);
                      return (
                        <td key={f} className={`px-3 py-2 text-right bg-blue-50 tabular-nums ${pctClass(n)}`}>
                          {pctText(n, raw)}
                        </td>
                      );
                    })}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ======================== Stock Trades ================================

const StockTrades = ({ symbol }) => {
  const TAB_CONFIG = [
    { key: "stockBulkDeals", label: "Bulk" },
    { key: "stockBlockDeals", label: "Block" },
    { key: "stockSastDeals", label: "SAST" },
    { key: "stockInsiderDeals", label: "Insider" },
  ];

  const PAGE_SIZE = 10;

  /* ---- Tiny helpers (no per-tab schema) ---- */
  const fmtNum = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n.toLocaleString("en-IN") : "—";
  };

  const fmtPrice = (v) => {
    const n = Number(v);
    return Number.isFinite(n)
      ? n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
      : "—";
  };

  // 25 Sept 2025 (uniform)
  const fmtDate = (d) => {
    const dt = new Date(d);
    return isNaN(dt)
      ? "—"
      : dt.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
  };

  const pctTxt = (v) => (v == null || isNaN(v) ? "—" : `${Number(v).toFixed(2)}%`);
  const pctCls = (v) =>
    v == null || isNaN(v) ? "text-gray-500" : Number(v) >= 0 ? "text-green-600" : "text-red-600";

  // Resolve fields that vary across tabs (simple OR-chains)
  const getClient = (r) => r.clientName || r.acquirerName || r.acqName || r.personName || "—";
  const getSide = (r) => r.buySell || r.acqSaleType || r.transactionType || "—";
  const getMode = (r) => r.acqMode || r.acquisitionMode || r.personCategory || r.relation || "—";
  const getQty = (r) => r.qty || r.noOfShareAcq || r.noOfShareSale || r.secAcq || null;
  const getAvg = (r) => r.watp || r.avgPrice || r.price || null;
  const getD1 = (r) => parseFloat(r.change_1D ?? r.change ?? r.chg ?? NaN);
  const getW1 = (r) => parseFloat(r.change_1W ?? NaN);

  const formatPersonName = (name) => {
    if (!name || name === "—") return name;

    return String(name)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\b[a-z]/g, (ch) => ch.toUpperCase());
  };

  const [data, setData] = useState({
    stockBulkDeals: { data: [], loading: true, error: null },
    stockBlockDeals: { data: [], loading: true, error: null },
    stockSastDeals: { data: [], loading: true, error: null },
    stockInsiderDeals: { data: [], loading: true, error: null },
  });

  const [activeKey, setActiveKey] = useState(TAB_CONFIG[0].key);

  // sorting (just 3 keys)
  const [sortKey, setSortKey] = useState("date"); // 'date' | 'd1' | 'w1'
  const [sortDir, setSortDir] = useState("desc"); // 'asc' | 'desc'

  // paging per tab
  const [pageByKey, setPageByKey] = useState(() =>
    TAB_CONFIG.reduce((acc, t) => {
      acc[t.key] = 1;
      return acc;
    }, {})
  );

  const currentPage = pageByKey[activeKey] || 1;
  const setPage = (key, page) => setPageByKey((prev) => ({ ...prev, [key]: page }));

  useEffect(() => {
    let cancelled = false;

    // reset
    setData({
      stockBulkDeals: { data: [], loading: true, error: null },
      stockBlockDeals: { data: [], loading: true, error: null },
      stockSastDeals: { data: [], loading: true, error: null },
      stockInsiderDeals: { data: [], loading: true, error: null },
    });

    TAB_CONFIG.forEach(({ key }) => {
      axios
        .get(`${process.env.REACT_APP_API_URL}/api_2/${key}/${symbol}`)
        .then((res) => {
          if (cancelled) return;
          setData((prev) => ({
            ...prev,
            [key]: { data: Array.isArray(res.data) ? res.data : [], loading: false, error: null },
          }));
        })
        .catch((err) => {
          if (cancelled) return;
          console.error(`Error fetching ${key}:`, err);
          setData((prev) => ({
            ...prev,
            [key]: { data: [], loading: false, error: "Failed to load" },
          }));
        });
    });

    return () => {
      cancelled = true;
    };
  }, [symbol]);

  const active = data[activeKey] || { data: [], loading: false, error: null };

  const sortedRows = useMemo(() => {
    const arr = [...(active.data || [])];

    if (sortKey === "date") {
      arr.sort((a, b) => {
        const ta = new Date(a?.date || 0).getTime();
        const tb = new Date(b?.date || 0).getTime();
        const diff = (isNaN(tb) ? 0 : tb) - (isNaN(ta) ? 0 : ta);
        return sortDir === "asc" ? -diff : diff; // default desc
      });
    } else if (sortKey === "d1") {
      arr.sort((a, b) => {
        const av = getD1(a);
        const bv = getD1(b);
        const na = isNaN(av) ? -Infinity : av;
        const nb = isNaN(bv) ? -Infinity : bv;
        return sortDir === "asc" ? na - nb : nb - na;
      });
    } else if (sortKey === "w1") {
      arr.sort((a, b) => {
        const av = getW1(a);
        const bv = getW1(b);
        const na = isNaN(av) ? -Infinity : av;
        const nb = isNaN(bv) ? -Infinity : bv;
        return sortDir === "asc" ? na - nb : nb - na;
      });
    }

    return arr;
  }, [active.data, sortKey, sortDir]);

  const totalPages = useMemo(() => {
    const n = sortedRows.length;
    return Math.max(1, Math.ceil(n / PAGE_SIZE)); // always at least 1
  }, [sortedRows.length]);

  const pageSafe = Math.min(Math.max(currentPage, 1), totalPages);

  const pagedRows = useMemo(() => {
    const start = (pageSafe - 1) * PAGE_SIZE;
    return sortedRows.slice(start, start + PAGE_SIZE);
  }, [sortedRows, pageSafe]);

  useEffect(() => {
    // keep page in range whenever tab/data changes
    if (currentPage > totalPages) setPage(activeKey, totalPages);
    if (currentPage < 1) setPage(activeKey, 1);
  }, [activeKey, totalPages]);

  const goPrev = () => setPage(activeKey, Math.max(1, pageSafe - 1));
  const goNext = () => setPage(activeKey, Math.min(totalPages, pageSafe + 1));

  const toggleSort = (key) => {
    // optional: reset to page 1 when sort changes (feels better)
    setPage(activeKey, 1);

    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const caret = (key) => (sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "");

  const PaginationBar = () => (
    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
      <div className="text-sm text-gray-500">
        Showing {pagedRows.length} of {sortedRows.length} items
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={goPrev}
          disabled={pageSafe <= 1}
          className="px-3 py-1.5 rounded-md border text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Prev
        </button>

        <span className="text-sm tabular-nums text-gray-700">
          Page <span className="font-medium">{pageSafe}</span> of{" "}
          <span className="font-medium">{totalPages}</span>
        </span>

        <button
          onClick={goNext}
          disabled={pageSafe >= totalPages}
          className="px-3 py-1.5 rounded-md border text-sm text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div className="w-full">
      <h4 className="text-lg font-semibold mb-3 mt-10">Trade Deals</h4>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b mb-3 pb-2">
        {TAB_CONFIG.map(({ key, label }) => {
          const isActive = key === activeKey;
          return (
            <button
              key={key}
              onClick={() => setActiveKey(key)}
              className={`px-3 py-1 text-sm rounded-t border-b-2 transition ${
                isActive
                  ? "border-blue-600 text-blue-700 font-semibold"
                  : "border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-300"
              }`}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Content */}
      {active.loading ? (
        <div className="flex items-center gap-2 text-blue-700">
          <span className="h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <span className="text-sm">Loading…</span>
        </div>
      ) : active.error ? (
        <div className="text-red-600 text-sm">No data available</div>
      ) : active.data.length === 0 ? (
        <>
          <PaginationBar />
          <div className="text-gray-500 text-sm">No records found.</div>
        </>
      ) : (
        <>
        <div className="px-3">
          <PaginationBar />
        </div>
        <div className="overflow-x-auto rounded-xl border">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 text-gray-700">
              <tr className="font-semibold">
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("date")}
                  title="Sort by Date"
                >
                  Date {caret("date")}
                </th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">
                  Buyer/Seller
                </th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Type</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Mode</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Qty</th>
                <th className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap">Avg/WATP</th>
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("d1")}
                  title="Sort by 1D"
                >
                  1D {caret("d1")}
                </th>
                <th
                  className="px-3 py-2 border border-gray-200 text-left whitespace-nowrap cursor-pointer select-none"
                  onClick={() => toggleSort("w1")}
                  title="Sort by 1W"
                >
                  1W {caret("w1")}
                </th>
              </tr>
            </thead>

            <tbody className="text-gray-700">
              {pagedRows.map((row, idx) => {
                const d1 = getD1(row);
                const w1 = getW1(row);
                const qty = getQty(row);
                const avg = getAvg(row);

                return (
                  <tr
                    key={row.hash ?? `${row.symbol}-${row.date}-${idx}`}
                    className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                  >
                    <td className="px-3 py-2 border border-gray-200 align-top">{fmtDate(row.date)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top">{formatPersonName(getClient(row))}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top">
                      <span
                        className={
                          (row.buySell || row.acqSaleType || "").toUpperCase() === "BUY"
                            ? "text-green-700"
                            : (row.buySell || row.acqSaleType || "").toUpperCase() === "SELL"
                            ? "text-red-700"
                            : "text-gray-700"
                        }
                      >
                        {getSide(row)}
                      </span>
                    </td>
                    <td className="px-3 py-2 border border-gray-200 align-top">{getMode(row)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top tabular-nums">{fmtNum(qty)}</td>
                    <td className="px-3 py-2 border border-gray-200 align-top tabular-nums">{fmtPrice(avg)}</td>

                    <td className={`px-3 py-2 border border-gray-200 align-top tabular-nums ${pctCls(d1)}`}>
                      {pctTxt(d1)}
                    </td>
                    <td className={`px-3 py-2 border border-gray-200 align-top tabular-nums ${pctCls(w1)}`}>
                      {pctTxt(w1)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
    
  );
};

// ======================== Financial Results ================================
/**
 * FinancialResults
 * - Keeps SAME UI/behavior
 * - Also limits columns to most recent 12 (rightmost) as you wanted earlier
 */
export function FinancialResults({
  consolidatedData = [],
  standaloneData = [],
  activeResultsTab,
  setActiveResultsTab,
  isMobile = false,
}) {
  const tableScrollRef = useRef(null);

  const metrics = [
    "Sales",
    "OperatingProfit",
    "OPM",
    "OtherIncome",
    "Interest",
    "Depreciation",
    "ProfitBeforeTax",
    "NetProfit",
    "EPS",
  ];

  const metricLabels = {
    Sales: "Sales",
    OperatingProfit: "Operating Profit",
    OPM: "OPM",
    OtherIncome: "Other Income",
    Interest: "Interest",
    Depreciation: "Depreciation",
    ProfitBeforeTax: "Profit Before Tax",
    NetProfit: "Net Profit",
    EPS: "EPS",
  };

  /*
   * Mobile labels use intentional line breaks so the first
   * column can be narrower without reducing font size.
   */
  const mobileMetricLabels = {
    Sales: ["Sales"],
    OperatingProfit: ["Operating", "Profit"],
    OPM: ["OPM"],
    OtherIncome: ["Other", "Income"],
    Interest: ["Interest"],
    Depreciation: ["Depreciation"],
    ProfitBeforeTax: ["Profit Before", "Tax"],
    NetProfit: ["Net", "Profit"],
    EPS: ["EPS"],
  };

  const boldMetrics = new Set([
    "OperatingProfit",
    "ProfitBeforeTax",
    "NetProfit",
  ]);

  const activeRowsAll =
    activeResultsTab === "consolidated"
      ? consolidatedData
      : standaloneData;

  const activeRows = useMemo(() => {
    const rows = Array.isArray(activeRowsAll)
      ? activeRowsAll
      : [];

    return rows.slice(-12);
  }, [activeRowsAll]);

  const hasAny =
    consolidatedData.length > 0 ||
    standaloneData.length > 0;

  const showTabs =
    consolidatedData.length > 0 &&
    standaloneData.length > 0;

  const parseQuarterDate = (dateValue) => {
    if (!dateValue) return null;

    let date;

    /*
     * Handles:
     * 31-Mar-2026
     * 31-03-2026
     * 31/03/2026
     * 31 Mar 2026
     */
    const match = String(dateValue)
      .trim()
      .match(
        /^(\d{1,2})[-/ ]([A-Za-z]{3}|\d{1,2})[-/ ](\d{4})$/
      );

    if (match) {
      const [, day, monthValue, year] = match;

      const monthNames = {
        jan: 0,
        feb: 1,
        mar: 2,
        apr: 3,
        may: 4,
        jun: 5,
        jul: 6,
        aug: 7,
        sep: 8,
        oct: 9,
        nov: 10,
        dec: 11,
      };

      const monthKey = monthValue.toLowerCase();

      const month = Number.isNaN(
        Number(monthValue)
      )
        ? monthNames[monthKey]
        : Number(monthValue) - 1;

      if (
        month === undefined ||
        month < 0 ||
        month > 11
      ) {
        return null;
      }

      date = new Date(
        Number(year),
        month,
        Number(day)
      );
    } else {
      date = new Date(dateValue);
    }

    if (Number.isNaN(date.getTime())) {
      return null;
    }

    return date;
  };

  const formatQuarterDate = (dateValue) => {
    const date = parseQuarterDate(dateValue);

    if (!date) {
      return {
        full: dateValue || "—",
        month: dateValue || "—",
        year: "",
      };
    }

    const month = date.toLocaleDateString(
      "en-US",
      {
        month: "short",
      }
    );

    const year = String(date.getFullYear());

    return {
      full: `${month} ${year}`,
      month,
      year,
    };
  };

  const formatBroadcastDate = (dateValue) => {
    if (!dateValue) return "";

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return dateValue;
    }

    return date.toLocaleDateString("en-GB");
  };

  const formatNumber = (value, metric) => {
    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return "—";
    }

    const numericValue =
      typeof value === "string"
        ? Number(
            value
              .replace(/,/g, "")
              .replace(/%/g, "")
              .trim()
          )
        : Number(value);

    if (!Number.isFinite(numericValue)) {
      return value;
    }

    let decimalPlaces;

    if (metric === "EPS") {
      decimalPlaces = 2;
    } else {
      decimalPlaces =
        Math.abs(numericValue) >= 1
          ? 0
          : 2;
    }

    const formattedValue =
      numericValue.toLocaleString("en-US", {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
      });

    return metric === "OPM"
      ? `${formattedValue}%`
      : formattedValue;
  };

  const renderMetricLabel = (metric) => {
    if (!isMobile) {
      return metricLabels[metric] || metric;
    }

    const lines =
      mobileMetricLabels[metric] || [
        metricLabels[metric] || metric,
      ];

    return (
      <span className="flex flex-col leading-5">
        {lines.map((line, index) => (
          <span key={`${metric}-${index}`}>
            {line}
          </span>
        ))}
      </span>
    );
  };

  /*
   * Focus the latest quarter columns.
   */
  useEffect(() => {
    const element = tableScrollRef.current;

    if (!element) return;

    const scrollToRight = () => {
      element.scrollLeft = Math.max(
        element.scrollWidth -
          element.clientWidth,
        0
      );
    };

    const firstFrame =
      requestAnimationFrame(() => {
        scrollToRight();

        /*
         * A second frame helps after mobile wrapping
         * and sticky-column layout are calculated.
         */
        requestAnimationFrame(scrollToRight);
      });

    return () => {
      cancelAnimationFrame(firstFrame);
    };
  }, [
    activeResultsTab,
    activeRows,
    isMobile,
  ]);

  /*
   * Keep text-sm on both mobile and desktop.
   * Only widths and padding change.
   */
  const firstColumnClass = isMobile
    ? "w-[106px] min-w-[106px] max-w-[106px] px-2"
    : "w-[150px] min-w-[150px] max-w-[150px] px-2";

  const quarterColumnClass = isMobile
    ? "w-[72px] min-w-[72px] max-w-[72px] px-1.5"
    : "w-[100px] min-w-[100px] max-w-[100px] px-2";

  const mobileTableWidth =
    106 + activeRows.length * 72;

  const tableStyle = isMobile
    ? {
        minWidth: `${mobileTableWidth}px`,
      }
    : undefined;

  return (
    <div>
      <div>
        <h4 className="mt-6 text-lg font-semibold">
          Quarterly Results

          <span className="ml-2 text-sm font-normal text-gray-400">
            (Rs. Crores)
          </span>
        </h4>

        {showTabs && (
          <div className="my-2 inline-flex rounded-lg border border-gray-300 bg-gray-100 p-0.5">
            <button
              type="button"
              onClick={() =>
                setActiveResultsTab(
                  "consolidated"
                )
              }
              className={`rounded-md px-3 py-1 text-xs transition-all duration-200 ${
                activeResultsTab ===
                "consolidated"
                  ? "bg-white font-semibold text-blue-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Consolidated
            </button>

            <button
              type="button"
              onClick={() =>
                setActiveResultsTab(
                  "standalone"
                )
              }
              className={`rounded-md px-3 py-1 text-xs transition-all duration-200 ${
                activeResultsTab ===
                "standalone"
                  ? "bg-white font-semibold text-blue-700 shadow-sm"
                  : "text-gray-600 hover:text-gray-900"
              }`}
            >
              Standalone
            </button>
          </div>
        )}

        <div className="text-xs font-normal text-gray-400">
          *Some results data might be
          inaccurate, we are working on this.
        </div>
      </div>

      {hasAny ? (
        <div
          ref={tableScrollRef}
          className="mt-2 mb-4 overflow-x-auto rounded-lg border border-gray-300"
        >
          <table
            style={tableStyle}
            className={`border-separate border-spacing-0 text-left text-sm ${
              isMobile
                ? "w-max"
                : "min-w-max"
            }`}
          >
            <thead className="bg-gray-100">
              <tr>
                <th
                  className={`sticky left-[-1px] z-20 border bg-gray-100 py-2 ${firstColumnClass}`}
                />

                {activeRows.map(
                  (row, index) => {
                    const quarter =
                      formatQuarterDate(
                        row?.toDate
                      );

                    return (
                      <th
                        key={`${
                          row?.toDate || "date"
                        }-${index}`}
                        className={`whitespace-nowrap border py-2 text-right ${quarterColumnClass}`}
                      >
                        {isMobile ? (
                          <div className="flex flex-col items-end font-semibold leading-5 text-gray-700">
                            <span>
                              {quarter.month}
                            </span>

                            <span>
                              {quarter.year}
                            </span>
                          </div>
                        ) : (
                          <>
                            <div className="font-semibold text-gray-700">
                              {quarter.full}
                            </div>

                            {row?.broadCastDate && (
                              <div className="mt-0.5 text-[11px] font-normal text-gray-400">
                                {formatBroadcastDate(
                                  row.broadCastDate
                                )}
                              </div>
                            )}
                          </>
                        )}
                      </th>
                    );
                  }
                )}
              </tr>
            </thead>

            <tbody>
              {metrics.map(
                (metric, rowIndex) => {
                  const isBold =
                    boldMetrics.has(metric);

                  const rowBackground =
                    rowIndex % 2 === 0
                      ? "bg-white"
                      : "bg-gray-100";

                  return (
                    <tr
                      key={metric}
                      className={`${rowBackground} ${
                        isBold
                          ? "font-semibold text-gray-900"
                          : ""
                      }`}
                    >
                      <td
                        className={`sticky left-[-1px] z-10 border py-1.5 text-sm ${rowBackground} ${firstColumnClass} ${
                          isBold
                            ? "font-bold text-gray-900"
                            : "font-medium text-gray-700"
                        }`}
                      >
                        {renderMetricLabel(
                          metric
                        )}
                      </td>

                      {activeRows.map(
                        (row, index) => (
                          <td
                            key={`${metric}-${index}`}
                            className={`whitespace-nowrap border py-1.5 text-right text-sm ${quarterColumnClass} ${
                              isBold
                                ? "font-bold"
                                : ""
                            }`}
                          >
                            {formatNumber(
                              row?.[metric],
                              metric
                            )}
                          </td>
                        )
                      )}
                    </tr>
                  );
                }
              )}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="mt-6 mb-4 text-sm italic text-gray-500">
          No financial data available
        </div>
      )}
    </div>
  );
}

const RangeSelector = ({
  timeFrames,
  rangePctChangeMap = {},
  selectedRange,
  setSelectedRange,
}) => {
  const pctChange = rangePctChangeMap?.[selectedRange];

  return (
    <div>
      {/* Selected Range Change */}
      {pctChange != null && Number.isFinite(pctChange) && (
      <div className="mb-1 text-sm font-medium">
        <span className="text-gray-500">
          {selectedRange} Change:{" "}
        </span>

        <span
          className={
            pctChange >= 0
              ? "text-green-600"
              : "text-red-600"
          }
        >
          {pctChange >= 0 ? "+" : ""}
          {pctChange.toFixed(2)}%
        </span>
      </div>
      )}

      {/* Buttons */}
      <div className="inline-flex flex-wrap gap-0 border border-gray-300 rounded-md overflow-hidden">
        {Object.keys(timeFrames).map((range, idx, arr) => {
          const isActive = selectedRange === range;
          const isFirst = idx === 0;
          const isLast = idx === arr.length - 1;

          return (
            <button
              key={range}
              onClick={() => setSelectedRange(range)}
              className={`transition-all duration-200 font-medium
                ${
                  isActive
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }
                ${!isLast ? "border-r border-gray-300" : ""}
                ${isFirst ? "rounded-l-md" : ""}
                ${isLast ? "rounded-r-md" : ""}
                px-3 py-1 text-sm sm:px-3.5 sm:py-1
              `}
            >
              {range}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ======================== Stock Graph ================================
const formatText = (value = "") => {
  return String(value)
    .replace(/_/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim();
};

const isValidUrl = (value) => {
  if (typeof value !== "string" || !value.trim()) {
    return false;
  }

  try {
    const parsedUrl = new URL(value.trim());

    return (
      parsedUrl.protocol === "http:" ||
      parsedUrl.protocol === "https:"
    );
  } catch {
    return false;
  }
};

const normalizeSources = (sources) => {
  if (!Array.isArray(sources)) {
    return [];
  }

  return sources
    .filter((source) => isValidUrl(source))
    .map((source) => source.trim());
};

const normalizeNodes = (items) => {
  if (!Array.isArray(items)) return [];

  return items
    .map((item, index) => {
      if (typeof item === "string") {
        return {
          key: `${item}-${index}`,
          name: item,
          url: "",
          img: "",
          src: [],
        };
      }

      if (item && typeof item === "object") {
        const name =
          item.name ||
          item.symbol ||
          item.title ||
          item.key ||
          `Node ${index + 1}`;

        return {
          ...item,

          key:
            item.key ||
            item.symbol ||
            item.name ||
            item.title ||
            `node-${index}`,

          name,

          url: isValidUrl(item.url)
            ? item.url.trim()
            : "",

          img: isValidUrl(item.img)
            ? item.img.trim()
            : "",

          src: normalizeSources(item.src),
        };
      }

      return null;
    })
    .filter(Boolean);
};

export function StockGraph({ data }) {
  const graphData = useMemo(() => {
    if (!data || typeof data !== "object") {
      return null;
    }

    const industryKey = Object.keys(data)[0];

    if (!industryKey) {
      return null;
    }

    return {
      industryKey,
      industryName: formatText(industryKey),
      categories: data[industryKey] || {},
    };
  }, [data]);

  const availableCategories = useMemo(() => {
    if (!graphData) return [];

    return Object.entries(graphData.categories).map(
      ([key, items]) => ({
        key,

        // Display label directly from JSON category key
        label: formatText(key),

        count: Array.isArray(items)
          ? items.length
          : 0,
      })
    );
  }, [graphData]);

  const firstNonEmptyCategory = useMemo(() => {
    return (
      availableCategories.find(
        (category) => category.count > 0
      )?.key || ""
    );
  }, [availableCategories]);

  const [selectedCategory, setSelectedCategory] =
    useState("");

  const selectedCategoryExists =
    availableCategories.some(
      (category) =>
        category.key === selectedCategory &&
        category.count > 0
    );

  const activeCategory = selectedCategoryExists
    ? selectedCategory
    : firstNonEmptyCategory;

  const nodes = useMemo(() => {
    if (!graphData || !activeCategory) {
      return [];
    }

    return normalizeNodes(
      graphData.categories[activeCategory]
    );
  }, [graphData, activeCategory]);

  if (!graphData) return null;

  return (
    <div className="w-full">
      <h4 className="mb-3 mt-10 text-lg font-semibold">
        Graphs
      </h4>

      {/* Category buttons */}
      <div className="flex gap-2 overflow-x-auto border-b border-gray-200 px-3 py-2.5 sm:flex-wrap sm:px-4">
        {availableCategories.map((category) => {
          const isActive =
            activeCategory === category.key;

          const isEmpty =
            category.count === 0;

          return (
            <button
              key={category.key}
              type="button"
              disabled={isEmpty}
              onClick={() =>
                setSelectedCategory(category.key)
              }
              className={[
                "shrink-0 rounded-full border px-2.5 py-1",
                "text-[11px] font-medium transition-colors",

                isActive
                  ? "border-blue-600 bg-blue-600 text-white"
                  : isEmpty
                    ? "cursor-not-allowed border-gray-200 bg-gray-50 text-gray-300"
                    : "border-gray-300 bg-white text-gray-700 hover:border-blue-500 hover:text-blue-600",
              ].join(" ")}
            >
              {category.label}

              {category.count > 0 && (
                <span
                  className={[
                    "ml-1.5 rounded-full px-1.5 py-0.5 text-[9px]",

                    isActive
                      ? "bg-white/20 text-white"
                      : "bg-gray-100 text-gray-500",
                  ].join(" ")}
                >
                  {category.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Graph */}
      <div className="p-3 sm:p-4">
        {nodes.length > 0 ? (
          <GraphCanvas
            industryName={graphData.industryName}
            categoryLabel={formatText(
              activeCategory
            )}
            nodes={nodes}
          />
        ) : (
          <div className="flex min-h-[220px] items-center justify-center rounded-xl bg-gray-50">
            <p className="text-sm text-gray-500">
              No nodes available for this category.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function GraphCanvas({
  industryName,
  categoryLabel,
  nodes,
}) {
  const width = 820;

  const height = Math.max(
    430,
    nodes.length * 58
  );

  const centerX = width / 2;
  const centerY = height / 2;

  const radiusX = Math.min(
    295,
    width * 0.36
  );

  const radiusY = Math.min(
    Math.max(155, nodes.length * 29),
    height * 0.39
  );

  const positions = useMemo(() => {
    return nodes.map((node, index) => {
      const angle =
        -Math.PI / 2 +
        (index * 2 * Math.PI) /
          nodes.length;

      return {
        ...node,

        x:
          centerX +
          radiusX * Math.cos(angle),

        y:
          centerY +
          radiusY * Math.sin(angle),
      };
    });
  }, [
    nodes,
    centerX,
    centerY,
    radiusX,
    radiusY,
  ]);

  return (
    <div className="overflow-x-auto rounded-xl border border-gray-200 bg-gray-50">
      <div
        className="relative mx-auto"
        style={{
          width: `${width}px`,
          height: `${height}px`,
          minWidth: `${width}px`,
        }}
      >
        {/* Connecting lines */}
        <svg
          className="pointer-events-none absolute inset-0 h-full w-full"
          viewBox={`0 0 ${width} ${height}`}
        >
          {positions.map((node) => (
            <line
              key={`line-${node.key}`}
              x1={centerX}
              y1={centerY}
              x2={node.x}
              y2={node.y}
              stroke="currentColor"
              strokeWidth="1.25"
              className="text-gray-300"
            />
          ))}
        </svg>

        {/* Central industry node */}
        <div
          className={[
            "absolute z-10 flex -translate-x-1/2 -translate-y-1/2",
            "h-28 w-44 flex-col items-center justify-center",
            "rounded-xl border-2 border-blue-500",
            "bg-white px-3 text-center shadow-md",
          ].join(" ")}
          style={{
            left: `${centerX}px`,
            top: `${centerY}px`,
          }}
        >
          {/* <span className="text-[10px] font-medium uppercase tracking-wide text-blue-500">
            Industry
          </span> */}

          <span className="mt-1 text-xs font-semibold text-gray-900">
            {industryName}
          </span>

          <span className="mt-2 rounded-full bg-blue-50 px-3 py-0.5 text-[12px] font-medium text-blue-700">
            {categoryLabel}
          </span>
        </div>

        {/* Outer nodes */}
        {positions.map((node) => (
          <GraphNode
            key={node.key}
            node={node}
          />
        ))}
      </div>
    </div>
  );
}

function GraphNode({ node }) {
  const [imageFailed, setImageFailed] =
    useState(false);

  const hasUrl = isValidUrl(node.url);

  const hasImage =
    isValidUrl(node.img) &&
    !imageFailed;

  const sources = normalizeSources(
    node.src
  );

  const nodeContent = (
    <>
      {hasImage && (
        <img
          src={node.img}
          alt={node.name}
          loading="lazy"
          onError={() =>
            setImageFailed(true)
          }
          className="h-9 w-9 shrink-0 rounded-md border border-gray-200 object-cover"
        />
      )}

      <div className="min-w-0 flex-1 text-center">
        <div
          className={[
            "break-words text-[11px] font-medium leading-4",

            hasUrl
              ? "text-blue-600 group-hover:underline"
              : "text-gray-700",
          ].join(" ")}
        >
          {node.name}
        </div>

        {sources.length > 0 && (
          <div className="mt-1 flex flex-wrap items-center justify-center gap-1">
            {sources.map(
              (source, index) => (
                <a
                  key={`${source}-${index}`}
                  href={source}
                  target="_blank"
                  rel="noopener noreferrer"
                  title={`Source ${index + 1}`}
                  aria-label={`Open source ${index + 1} for ${node.name}`}
                  onClick={(event) =>
                    event.stopPropagation()
                  }
                  className="text-[9px] font-medium text-blue-600 underline decoration-blue-300 underline-offset-2 hover:text-blue-800"
                >
                  [{index + 1}]
                </a>
              )
            )}
          </div>
        )}
      </div>
    </>
  );

  return (
    <div
      title={node.name}
      className={[
        "absolute z-10 flex min-h-[58px] w-40",
        "-translate-x-1/2 -translate-y-1/2",
        "items-center justify-center rounded-lg",
        "border border-gray-300 bg-white px-2 py-1.5",
        "text-center shadow-sm transition-all",
        "hover:-translate-y-[55%] hover:border-blue-400 hover:shadow-md",
      ].join(" ")}
      style={{
        left: `${node.x}px`,
        top: `${node.y}px`,
      }}
    >
      {hasUrl ? (
        <a
          href={node.url}
          target="_blank"
          rel="noopener noreferrer"
          className="group flex w-full items-center gap-2"
        >
          {nodeContent}
        </a>
      ) : (
        <div className="flex w-full items-center gap-2">
          {nodeContent}
        </div>
      )}
    </div>
  );
}

// ======================== Stock Links ================================

export function StockLink({ data = [] }) {
  const grouped = useMemo(() => {
    return data.reduce((acc, item) => {
      if (!acc[item.section]) acc[item.section] = [];
      acc[item.section].push(item);
      return acc;
    }, {});
  }, [data]);

  const sections = Object.keys(grouped);

  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    if (sections.length && !sections.includes(activeSection)) {
      setActiveSection(sections[0]);
    }
  }, [sections, activeSection]);

  if (!data.length) return null;

  return (
    <div className="w-full">
      <h4 className="text-lg font-semibold mb-3 mt-10">Links</h4>

      {/* Tabs */}
      <div className="flex flex-wrap gap-2 border-b mb-4 pb-2">
        {sections.map((section) => (
          <button
            key={section}
            onClick={() => setActiveSection(section)}
            className={`px-3 py-1 text-sm rounded-t border-b-2 transition ${
              activeSection === section
                ? "border-blue-600 text-blue-700 font-semibold"
                : "border-transparent text-gray-600 hover:text-blue-600 hover:border-blue-300"
            }`}
          >
            {section}
          </button>
        ))}
      </div>

      {/* Links */}
      <ul className="list-disc pl-5 space-y-2">
        {(grouped[activeSection] || []).map((link, i) => (
          <li key={i}>
            <a
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 hover:underline"
            >
              {link.details}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ============================== Stock Holding ==============================

export function StockShareHolding({
  data,
  isMobile = false,
}) {
  const [expandedGroups, setExpandedGroups] = useState({});
  const tableScrollRef = useRef(null);

  const parseValue = (value) => {
    const parsedValue = parseFloat(
      String(value ?? "0")
        .replace("%", "")
        .replace(/,/g, "")
        .trim()
    );

    return Number.isFinite(parsedValue) ? parsedValue : 0;
  };

  const tableData = useMemo(() => {
    if (!data?.dates?.length || !data?.groups) {
      return null;
    }

    const startIndex = Math.max(
      data.dates.length - 12,
      0
    );

    const visibleDates = data.dates.slice(startIndex);

    const preferredOrder = [
      "Promoters",
      "FIIs",
      "DIIs",
      "Public",
      "Government",
      "Others",
    ];

    const getMostRecentNonZeroHolding = (holder) => {
      for (
        let index = visibleDates.length - 1;
        index >= 0;
        index -= 1
      ) {
        const value = parseValue(
          holder.values?.[index]
        );

        if (value !== 0) {
          return {
            value,
            index,
          };
        }
      }

      return {
        value: 0,
        index: -1,
      };
    };

    const rows = preferredOrder
      .map((type) => {
        const group = data.groups[type];
        const total = group?.total;

        if (!total?.name?.endsWith("_Total")) {
          return null;
        }

        const totalValues = Array.isArray(total.values)
          ? total.values.slice(startIndex)
          : [];

        /*
         * Hide the complete main row when every
         * visible quarter is zero.
         */
        const hasAnyNonZeroTotal = visibleDates.some(
          (_, index) =>
            parseValue(totalValues[index]) !== 0
        );

        if (!hasAnyNonZeroTotal) {
          return null;
        }

        const holders = Array.isArray(group.holders)
          ? group.holders
              .map((holder) => ({
                ...holder,
                values: Array.isArray(holder.values)
                  ? holder.values.slice(startIndex)
                  : [],
              }))
              /*
               * Hide shareholder rows when all
               * visible-quarter values are zero.
               */
              .filter((holder) =>
                visibleDates.some(
                  (_, index) =>
                    parseValue(
                      holder.values?.[index]
                    ) !== 0
                )
              )
              /*
               * Sort using the most recent non-zero
               * percentage.
               */
              .sort((a, b) => {
                const aLatest =
                  getMostRecentNonZeroHolding(a);

                const bLatest =
                  getMostRecentNonZeroHolding(b);

                if (
                  aLatest.value !== bLatest.value
                ) {
                  return (
                    bLatest.value - aLatest.value
                  );
                }

                if (
                  aLatest.index !== bLatest.index
                ) {
                  return (
                    bLatest.index - aLatest.index
                  );
                }

                return String(
                  a.name ?? ""
                ).localeCompare(
                  String(b.name ?? "")
                );
              })
          : [];

        return {
          type,
          label: total.name.replace(
            /_Total$/i,
            ""
          ),
          totalValues,
          holders,
        };
      })
      .filter(Boolean);

    return {
      dates: visibleDates,
      rows,
    };
  }, [data]);

  /*
   * On mobile, initially show the latest/right-most
   * quarters.
   *
   * This hook must remain before any conditional return.
   */
  useEffect(() => {
    if (!isMobile || !tableData) {
      return;
    }

    const container = tableScrollRef.current;

    if (!container) {
      return;
    }

    const scrollToLatest = () => {
      container.scrollLeft = Math.max(
        container.scrollWidth -
          container.clientWidth,
        0
      );
    };

    const animationFrameId =
      requestAnimationFrame(() => {
        scrollToLatest();

        /*
         * Run once more after the browser finishes
         * calculating sticky cells and wrapped text.
         */
        requestAnimationFrame(scrollToLatest);
      });

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [
    isMobile,
    tableData?.dates?.length,
    tableData?.rows?.length,
  ]);

  const toggleGroup = (type) => {
    setExpandedGroups((previous) => ({
      ...previous,
      [type]: !previous[type],
    }));
  };

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return {
        month: "",
        year: "",
        full: "",
      };
    }

    const parsedDate = new Date(dateValue);

    if (Number.isNaN(parsedDate.getTime())) {
      return {
        month: dateValue,
        year: "",
        full: dateValue,
      };
    }

    const month = parsedDate.toLocaleDateString(
      "en-US",
      {
        month: "short",
      }
    );

    const year = parsedDate.toLocaleDateString(
      "en-US",
      {
        year: "numeric",
      }
    );

    return {
      month,
      year,
      full: `${month} ${year}`,
    };
  };

  /*
   * Convert:
   * "NEERAJ KANWAR" -> "Neeraj Kanwar"
   */
  const formatHolderName = (name) => {
    if (!name) return "";

    return String(name)
      .trim()
      .toLowerCase()
      .replace(/\s+/g, " ")
      .replace(/\b[a-z]/g, (character) =>
        character.toUpperCase()
      );
  };

  const getValue = (values, index) => {
    const value = values?.[index];

    if (
      value === undefined ||
      value === null ||
      value === ""
    ) {
      return "";
    }

    if (parseValue(value) === 0) {
      return "";
    }

    return value;
  };

  if (
    !data ||
    !tableData ||
    tableData.rows.length === 0
  ) {
    return null;
  }

  const firstColumnClass = isMobile
    ? "w-[140px] min-w-[140px] max-w-[140px] px-2"
    : "w-[190px] min-w-[190px] max-w-[190px] px-4";

  const quarterColumnClass = isMobile
    ? "w-[76px] min-w-[76px] max-w-[76px] px-2"
    : "w-[105px] min-w-[105px] max-w-[105px] px-4";

  /*
   * Calculated minimum table width:
   *
   * Mobile:
   *   140px first column + 76px per quarter
   *
   * Desktop:
   *   Original minimum width remains 850px.
   */
  const mobileTableWidth =
    140 + tableData.dates.length * 76;

  const tableStyle = isMobile
    ? {
        minWidth: `${mobileTableWidth}px`,
      }
    : undefined;

  return (
    <section className="mt-8">
      <div className="mb-4">
        <h4 className="mt-3 text-lg font-semibold">
          Shareholding Pattern
        </h4>
      </div>

      <div
        ref={tableScrollRef}
        className="w-full overflow-x-auto"
      >
        <table
          style={tableStyle}
          className={`w-full border-collapse text-sm ${
            isMobile ? "" : "min-w-[850px]"
          }`}
        >
          <thead>
            <tr className="border-b border-gray-200">
              <th
                className={`sticky left-0 z-20 bg-white py-2.5 text-left ${firstColumnClass}`}
              >
                &nbsp;
              </th>

              {tableData.dates.map((date) => {
                const formattedDate =
                  formatDate(date);

                return (
                  <th
                    key={date}
                    className={`whitespace-nowrap py-2.5 text-right font-medium text-gray-600 ${quarterColumnClass}`}
                  >
                    {isMobile ? (
                      <span className="flex flex-col items-end leading-5">
                        <span>
                          {formattedDate.month}
                        </span>

                        <span>
                          {formattedDate.year}
                        </span>
                      </span>
                    ) : (
                      formattedDate.full
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>

          <tbody>
            {tableData.rows.map((row) => {
              const isExpanded = Boolean(
                expandedGroups[row.type]
              );

              const hasHolders =
                row.holders.length > 0;

              return (
                <React.Fragment key={row.type}>
                  <tr
                    className={`group border-b border-gray-100 transition-colors hover:bg-blue-50 ${
                      isExpanded
                        ? "font-bold"
                        : ""
                    }`}
                  >
                    <td
                      className={`sticky left-0 z-10 bg-white py-2 transition-colors group-hover:bg-blue-50 ${firstColumnClass}`}
                    >
                      <button
                        type="button"
                        onClick={() => {
                          if (hasHolders) {
                            toggleGroup(row.type);
                          }
                        }}
                        disabled={!hasHolders}
                        className={`flex w-full items-start gap-1.5 text-left text-sm leading-5 text-gray-800 disabled:cursor-default ${
                          isExpanded
                            ? "font-bold"
                            : "font-medium"
                        }`}
                        aria-expanded={isExpanded}
                      >
                        <span className="min-w-0 break-words">
                          {row.label}
                        </span>

                        {hasHolders && (
                          <span
                            className="flex h-4 w-4 shrink-0 items-center justify-center text-sm font-semibold text-blue-600"
                            aria-hidden="true"
                          >
                            {isExpanded
                              ? "−"
                              : "+"}
                          </span>
                        )}
                      </button>
                    </td>

                    {tableData.dates.map(
                      (date, index) => (
                        <td
                          key={`${row.type}-${date}`}
                          className={`whitespace-nowrap py-2 text-right text-sm leading-5 text-gray-800 transition-colors group-hover:bg-blue-50 ${quarterColumnClass} ${
                            isExpanded
                              ? "font-bold"
                              : "font-medium"
                          }`}
                        >
                          {getValue(
                            row.totalValues,
                            index
                          )}
                        </td>
                      )
                    )}
                  </tr>

                  {isExpanded &&
                    row.holders.map(
                      (
                        holder,
                        holderIndex
                      ) => (
                        <tr
                          key={`${row.type}-${holder.name}-${holderIndex}`}
                          className="group border-b border-gray-100 transition-colors hover:bg-blue-50"
                        >
                          <td
                            className={`sticky left-0 z-10 bg-white py-2 transition-colors group-hover:bg-blue-50 ${firstColumnClass}`}
                          >
                            <div
                              className={`text-sm leading-5 text-gray-700 ${
                                isMobile
                                  ? "pl-3"
                                  : "pl-5"
                              }`}
                            >
                              <span className="block break-words">
                                {formatHolderName(
                                  holder.name
                                )}
                              </span>
                            </div>
                          </td>

                          {tableData.dates.map(
                            (
                              date,
                              valueIndex
                            ) => (
                              <td
                                key={`${row.type}-${holder.name}-${date}`}
                                className={`whitespace-nowrap py-2 text-right text-sm leading-5 text-gray-600 transition-colors group-hover:bg-blue-50 ${quarterColumnClass}`}
                              >
                                {getValue(
                                  holder.values,
                                  valueIndex
                                )}
                              </td>
                            )
                          )}
                        </tr>
                      )
                    )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

// ============================== Main Page ==============================

function SymbolPage() {
  const [candles, setCandles] = useState({ close: [], volume: [] });
  const [loading, setLoading] = useState(true);
  const [stockInfoError, setStockInfoError] = useState(false);
  const [stockInfo, setStockInfo] = useState(null);
  const [tickPrefix, setTickPrefix] = useState("");
  const [selectedRange, setSelectedRange] = useState('1Y');
  const [consolidatedData, setConsolidatedData] = useState([]);
  const [standaloneData, setStandaloneData] = useState([]);
  const [newsData, setNewsData] = useState([]);
  const [newsFeedData, setNewsFeedData] = useState([]);
  const [stockEventsData, setStockEventsData] = useState([]);
  const [stockAnnouncementsData, setStockAnnouncementsData] = useState([]);
  const [stockCreditRatings, setStockCreditRatings] = useState([]);
  const [stockBrokerageReports, setStockBrokerageReports] = useState([]);

  const [sectorInfoData, setSectorInfoData] = useState([]);
  const [stockGraphData, setStockGraphData] = useState(null);
  const [stockLinksData, setStockLinksData] = useState([]);
  const [stockArticleData, setStockArticleData] = useState(null);
  const [stockShareHoldingData, setStockShareHoldingData] = useState(null);

  const [activeResultsTab, setActiveResultsTab] = useState('consolidated');
  const [clickedMsg, setClickedMsg] = useState(null);
  const [activeDotKey, setActiveDotKey] = useState(null);
  const { isMobile } = useDevice();
  const { symbol } = useParams();
  let decimalPoints = 2;
  let isSector = false;

  if(symbol.endsWith('USD') && symbol.length === 6)
  {
    decimalPoints = 4;
  }

  if(symbol.endsWith('_SECTOR'))
  {
    isSector = true;
  }

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/candles/${symbol}`)
      .then(res => {
        setCandles(res.data);
      })
      .catch(error => {
        console.error('Error fetching data', error);
      });

      if (symbol) {
        document.title = `${symbol} | Scan360`;
      }
      return () => {
        document.title = "Scan360"; // Optional cleanup
      };
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/info/${symbol}`)
      .then(res => {
        setStockInfo(res.data)
        setLoading(false);
        const quoteType = res.data.quoteType;
        const currency = res.data.currency;

        setTickPrefix(getCurrencySymbol(currency))
      })
      .catch(err => {
        console.error('Error fetching stock info:', err);
        setStockInfo(null);
        setStockInfoError(true);
        setLoading(false);
      });

  }, [symbol]);

  //===========================================================
  useEffect(() => {
    // Reset tab whenever the symbol changes
    setActiveResultsTab("consolidated");

    // Optional: clear old data to avoid showing previous stock
    setConsolidatedData([]);

    axios
      .get(
        `${process.env.REACT_APP_API_URL}/api/results/consolidated/${symbol}`
      )
      .then((res) => {
        const sortedData = res.data.sort(
          (a, b) => new Date(a.toDate) - new Date(b.toDate)
        );

        setConsolidatedData(sortedData);

        // If there is no consolidated data but standalone exists,
        // the standalone effect can switch the tab later.
      })
      .catch((err) => {
        console.error(
          "Error fetching consolidated data:",
          err
        );

        setConsolidatedData([]);
      });
  }, [symbol]);

  useEffect(() => {
  // Optional: clear old data
  setStandaloneData([]);

  axios
    .get(
      `${process.env.REACT_APP_API_URL}/api/results/standalone/${symbol}`
    )
    .then((res) => {
      const sortedData = res.data.sort(
        (a, b) => new Date(a.toDate) - new Date(b.toDate)
      );

      setStandaloneData(sortedData);
    })
    .catch((err) => {
      console.error(
        "Error fetching standalone data:",
        err
      );

      setStandaloneData([]);
    });
  }, [symbol]);

  useEffect(() => {
    // Every new symbol should begin with consolidated.
    setActiveResultsTab("consolidated");
  }, [symbol]);

  useEffect(() => {
    const hasConsolidated =
      Array.isArray(consolidatedData) &&
      consolidatedData.length > 0;

    const hasStandalone =
      Array.isArray(standaloneData) &&
      standaloneData.length > 0;

    if (!hasConsolidated && hasStandalone) {
      setActiveResultsTab("standalone");
    }
  }, [consolidatedData, standaloneData]);
  //===========================================================

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/news/${symbol}`)
      .then(res => {

        console.log("CANDLE API RAW:", {
        symbol,
        datesLength: res.data?.date?.length,
        closeLength: res.data?.close?.length,
        firstDate: res.data?.date?.[0],
        lastDate: res.data?.date?.[response.data?.date?.length - 1],
        });

        setNewsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching consolidated data:', err);
        setNewsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/graphs/${symbol}`)
      .then(res => {
        setStockGraphData(res.data);
      })
      .catch(err => {
        console.error('Error fetching graph data:', err);
        setStockGraphData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/links/${symbol}`)
      .then(res => {
        setStockLinksData(res.data);
      })
      .catch(err => {
        console.error('Error fetching graph data:', err);
        setStockLinksData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/shareholding/${symbol}`)
      .then(res => {
        setStockShareHoldingData(res.data);
      })
      .catch(err => {
        console.error('Error fetching graph data:', err);
        setStockShareHoldingData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/articles/${symbol}`)
      .then(res => {
        setStockArticleData(res.data);
      })
      .catch(err => {
        console.error('Error fetching graph data:', err);
        setStockArticleData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api/news_feed/${symbol}`)
      .then(res => {
        setNewsFeedData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setNewsFeedData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockEvents/${symbol}`)
      .then(res => {
        setStockEventsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockEventsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockAnnouncements/${symbol}`)
      .then(res => {
        setStockAnnouncementsData(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockAnnouncementsData([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockCreditRatings/${symbol}`)
      .then(res => {
        setStockCreditRatings(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockCreditRatings([]);
      });
  }, [symbol]);

  useEffect(() => {
    axios.get(`${process.env.REACT_APP_API_URL}/api_2/stockBrokerReports/${symbol}`)
      .then(res => {
        setStockBrokerageReports(res.data);
      })
      .catch(err => {
        console.error('Error fetching news feed data:', err);
        setStockBrokerageReports([]);
      });
  }, [symbol]);

  useEffect(() => {
    if(isSector)
    {
      axios.get(`${process.env.REACT_APP_API_URL}/api/sector_info/${symbol}`)
        .then(res => {
          setSectorInfoData(res.data);
        })
        .catch(err => {
          console.error('Error fetching news feed data:', err);
          setSectorInfoData([]);
        });
    }
  }, [symbol]);


  // build a unique key for each event (adjust if your data shape differs)
  const eventKey = useCallback((ev) => {
    return `${ev.date}__${ev.type || ""}__${ev.url || ""}`;
  }, []);

  // Utility function to generate N ticks between min and max
  const generateTicks = (min, max, count, toFixed = null) => {
    if (min === max) {
      // Prevent division by zero; add ±1 step around flat value
      return Array.from({ length: count }, (_, i) => min + (i - Math.floor(count / 2)));
    }

    const step = (max - min) / (count - 1);
    return Array.from({ length: count }, (_, i) => {
      const val = min + i * step;
      return toFixed !== null ? Number(val.toFixed(toFixed)) : Math.round(val);
    });
  };

  const getDMA = (index, window) => {
    if (!Array.isArray(candles?.close) || candles.close.length === 0) {
      return null;
    }

    if (index < 0 || index >= candles.close.length) {
      return null;
    }

    const multiplier = 2 / (window + 1);

    const firstClose = Number(candles.close[0]);

    if (!Number.isFinite(firstClose)) {
      return null;
    }

    let dma = firstClose;

    for (let i = 1; i <= index; i++) {
      const close = Number(candles.close[i]);

      if (!Number.isFinite(close)) {
        continue;
      }

      dma = (close - dma) * multiplier + dma;
    }

    return dma;
  };

  const getChartData = () => {
    const totalLength = candles.close.length;
    const limit = timeFrames[selectedRange];
    const start = Math.max(0, totalLength - limit);

    const hasTrades = Array.isArray(candles.trades);
    const hasOI = Array.isArray(candles.OI);

    // ✅ NEW: Options OI arrays
    const hasCEOI = Array.isArray(candles.CE_OI);
    const hasPEOI = Array.isArray(candles.PE_OI);
    const hasMarginVolume = Array.isArray(candles.Margin_Volume);
    const hasHaircut = Array.isArray(candles.Haircut);
  

    return candles.close.slice(start).map((close, i) => {
      const idx = start + i;

      const volume = Number(candles.volume?.[idx] ?? 0);

      const deliveryQtyRaw = Array.isArray(candles.delivery)
        ? candles.delivery[idx]
        : null;
      const deliveryQty = deliveryQtyRaw == null ? null : Number(deliveryQtyRaw);

      const hasDeliveryThisRow =
        Number.isFinite(deliveryQty) && Number.isFinite(volume) && volume > 0;

      const deliveredVol = hasDeliveryThisRow
        ? Math.max(0, Math.min(volume, deliveryQty))
        : 0;

      const undeliveredVol = hasDeliveryThisRow
        ? Math.max(0, volume - deliveredVol)
        : volume;

      const tradesRaw = hasTrades ? candles.trades[idx] : null;
      const trades = tradesRaw == null ? null : Number(tradesRaw);

      const ats =
        trades != null && trades > 0 && Number.isFinite(volume)
          ? volume / trades
          : null;

      const dma50 = getDMA(idx, 50);
      const dma200 = getDMA(idx, 200);

      // ✅ OI% (from backend) — allow null/undefined
      const oiRaw = hasOI ? candles.OI?.[idx] : null;
      const oi = oiRaw == null ? null : Number(oiRaw);
      const oiPct = Number.isFinite(oi) ? oi : null;

      // ✅ NEW: CE/PE OI + client-side PCR_OI
      const ceRaw = hasCEOI ? candles.CE_OI?.[idx] : null;
      const peRaw = hasPEOI ? candles.PE_OI?.[idx] : null;

      const ceOi = ceRaw == null ? null : Number(ceRaw);
      const peOi = peRaw == null ? null : Number(peRaw);

      const ceOiSafe = Number.isFinite(ceOi) ? ceOi : null;
      const peOiSafe = Number.isFinite(peOi) ? peOi : null;

      // original pcr meaning if > 1 PE more, if < 1, CE more
      const pcrOi =
        ceOiSafe != null && ceOiSafe > 0 && peOiSafe != null
          ? (peOiSafe / ceOiSafe) - 1   // ✅ normalize around 1
          : null;

      const marginRaw = hasMarginVolume
        ? candles.Margin_Volume[idx]
        : null;

      const marginVolume =
        marginRaw === null || marginRaw === undefined || marginRaw === ""
          ? null
          : Number(marginRaw);

      const marginVolumeSafe =
        Number.isFinite(marginVolume) && marginVolume >= 0
          ? marginVolume
          : null;

      // --------------------------------------------
      // Haircut (%) from backend
      // --------------------------------------------

      const haircutRaw = hasHaircut
        ? candles.Haircut[idx]
        : null;

      const haircut =
        haircutRaw === null ||
        haircutRaw === undefined ||
        haircutRaw === ""
          ? null
          : Number(haircutRaw);

      const haircutSafe =
        Number.isFinite(haircut) && haircut >= 0
          ? haircut
          : null;


      return {
        date: toYMD(candles.date?.[idx]),
        close: Number(close),
        volume,
        delivery: deliveryQty,
        deliveredVol,
        undeliveredVol,
        trades,
        ats,
        dma50,
        dma200,
        oi: oiPct,
        ceOi: ceOiSafe,
        peOi: peOiSafe,
        pcrOi,

        marginVolume: marginVolumeSafe,
        haircut: haircutSafe, // NEW
      };
    });
  };

  const getRangePctChangeMap = (candles, timeFrames) => {
    const closes = candles?.close || [];
    const dates = candles?.date || [];

    if (!closes.length) return {};

    const fullData = closes
      .map((close, idx) => ({
        date: toYMD(dates?.[idx]),
        close: Number(close),
      }))
      .filter((d) => d.date && Number.isFinite(d.close) && d.close > 0);

    if (fullData.length < 2) return {};

    const latest = fullData[fullData.length - 1];

    return Object.keys(timeFrames).reduce((acc, range) => {
      const limit = timeFrames[range];

      const startIndex =
        limit === Infinity || limit == null
          ? 0
          : Math.max(0, fullData.length - limit);

      const start = fullData[startIndex];

      if (!start?.close || !latest?.close) {
        acc[range] = null;
      } else {
        acc[range] = ((latest.close - start.close) / start.close) * 100;
      }

      return acc;
    }, {});
  };


  // if date fall on saturday or sunday, if will fall to monday
  // If event date falls on a non-trading day,
  // use the next available trading day.
  const getCloseByDate = (data, dateStr) => {
    if (!data?.length || !dateStr) return null;

    const toYMD = (date) =>
      String(date || "").trim().slice(0, 10);

    const target = toYMD(dateStr);
    if (!target) return null;

    // Exact trading day
    const exact = data.find(
      d => toYMD(d.date) === target
    );

    if (exact) {
      return {
        date: exact.date,
        close: exact.close,
      };
    }

    // Next available trading day
    const future = data.find(
      d => toYMD(d.date) > target
    );

    return future
      ? {
          date: future.date,
          close: future.close,
        }
      : null;
  };


  // 1) chartData memo
  const chartData = useMemo(() => getChartData(), [candles, selectedRange]);
  const rangePctChangeMap = useMemo(() => {
    return getRangePctChangeMap(candles, timeFrames);
  }, [candles, timeFrames]);


  // 2) derived ranges memo
  const closePrices = useMemo(() => chartData.map(d => d.close), [chartData]);
  const rawYMin = useMemo(() => Math.min(...closePrices), [closePrices]);
  const rawYMax = useMemo(() => Math.max(...closePrices), [closePrices]);

  const [yMin, yMax] = useMemo(
    () => addPadding(rawYMin, rawYMax, 0.05), // 5% padding
    [rawYMin, rawYMax]
  );

  const volumeData = useMemo(() => chartData.map(d => d.volume), [chartData]);
  const vMin = useMemo(() => Math.min(...volumeData), [volumeData]);
  const vMax = useMemo(() => Math.max(...volumeData), [volumeData]);

  // 3) ticks memo
  const priceTicks = useMemo(
    () => generateTicks(yMin, yMax, 8, decimalPoints),
    [yMin, yMax, decimalPoints]
  );
  const volumeTicks = useMemo(
    () => generateTicks(vMin, vMax, 8),
    [vMin, vMax]
  );
  const xTicks = useMemo(() => {
    const xTickCount = 6;
    const interval = Math.max(1, Math.floor(chartData.length / (xTickCount - 1)));
    const ticks = chartData.filter((_, i) => i % interval === 0).map(d => d.date);
    if (chartData.length > 0 && ticks[ticks.length - 1] !== chartData[chartData.length - 1].date) {
      ticks.push(chartData[chartData.length - 1].date);
    }
    return ticks;
  }, [chartData]);

  // margin={{ top: 6, right: 8, left: 8, bottom: 0 }}
  // 4) stable margin + click handler
  const chartMargin = useMemo(
    () => ({ top: 6, right: isMobile ? 10 : 20, left: isMobile ? 10 : 20, bottom: 0 }),
    [isMobile]
  );

  // Avoid setting state if the same event is clicked again (prevents useless parent re-renders)
  const handleDotClick = useCallback((msg) => {
    const normalizedMsg = {
      ...msg,
      date: msg.xDate || msg.date, // use chart/trading date if available
    };

    setClickedMsg(prev =>
      prev &&
      prev.type === normalizedMsg.type &&
      prev.date === normalizedMsg.date &&
      prev.price === normalizedMsg.price &&
      prev.url === normalizedMsg.url &&
      prev.text === normalizedMsg.text
        ? prev
        : normalizedMsg
    );

    setActiveDotKey(eventKey(normalizedMsg));
  }, [eventKey]);

  if (loading) return <div className="p-4">Loading...</div>;

  if(stockInfoError) return <div className="p-4">Cannot find symbol {symbol} </div>;
  // if (!candles.close || candles.close.length === 0) return <div className="p-4">No data available</div>;

  return (
    <div className="p-4 mb-10 overflow-x-hidden">
      <StockHeader
        stockInfo={stockInfo}
        symbol={symbol}
        tickPrefix={tickPrefix}
        decimalPoints={decimalPoints}
      />

      {/* Timeline Buttons */}
      <RangeSelector
        timeFrames={timeFrames}
        rangePctChangeMap={rangePctChangeMap}
        selectedRange={selectedRange}
        setSelectedRange={setSelectedRange}
      />

      {/* Sticky message box above the chart */}
      <div className="mb-2">   {/* 👈 reserve ~80px */}
        {clickedMsg ? (
          <div className="w-full max-w-full md:max-w-3xl px-3 py-2 border rounded-lg bg-white shadow-sm text-sm">
            {/* Row 1: type + meta */}
            <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-1">
              {/* Type chip */}
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                {clickedMsg.type}
              </span>

              {/* Date */}
              <span className="font-mono text-gray-700">
                {clickedMsg.date}
              </span>

              {/* Price */}
              <span className="font-mono text-gray-900">
                {tickPrefix}{Number(clickedMsg.price).toFixed(decimalPoints)}
              </span>
            </div>

            {/* Row 2: details (wraps + scrolls if very long) */}
            <div>
              <span className="text-gray-700 whitespace-pre-wrap break-words max-h-28 overflow-auto">
                {clickedMsg.text}
              </span>

              {/* Row 3: link (breaks on small screens) */}
              <span className="ml-1">
              {clickedMsg.url && (
                <a
                  href={clickedMsg.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 inline-block text-blue-600 underline break-all"
                >
                  source
                </a>
              )}
              </span>
            </div>
          </div>
        ) : (
          <div className="inline-flex items-start gap-2 sm:gap-3 px-3 py-2 border rounded bg-white shadow-sm text-sm">
            💡 Tap a marker on the chart to show event details.
          </div>
        )}
      </div>

      <SeasonalityMatrix 
        chartData={chartData}
        stockInfo={stockInfo}
        isMobile={isMobile}
      />

      {/* Stock Chart */}
      <PriceChart
        chartData={chartData}
        stockInfo={stockInfo}
        isMobile={isMobile}
        margin={chartMargin}
        xTicks={xTicks}
        volumeTicks={volumeTicks}
        priceTicks={priceTicks}
        yMin={yMin}
        yMax={yMax}
        decimalPoints={decimalPoints}
        tickPrefix={tickPrefix}
        newsData={newsData}
        onDotClick={handleDotClick}
        getCloseByDate={getCloseByDate}
        activeDotKey={activeDotKey}   // 👈
        eventKey={eventKey}           // 👈
      />

      {/* Financial Impacts */}
      {stockInfo && <FinancialImpacts stockInfo={stockInfo} />}
      
      {stockInfo && stockInfo.quoteType === "SECTOR" && (
      <SectorInfoTable sectorResp={sectorInfoData} isMobile={isMobile} />
      )}

      {/* News & Stories */}
      <NewsFeedTable data={newsFeedData} />

      {stockInfo && stockInfo.quoteType === "EQUITY" && (
        <>
          <FinancialResults
            consolidatedData={consolidatedData}
            standaloneData={standaloneData}
            activeResultsTab={activeResultsTab}
            setActiveResultsTab={setActiveResultsTab}
            isMobile={isMobile}
          />

          <StockEvents data={stockEventsData} />
          <StockBrokerageReports data={stockBrokerageReports} />
          <StockCreditRatings data={stockCreditRatings} />
          <StockAnnouncements data={stockAnnouncementsData} />
          <StockShareHolding data={stockShareHoldingData} isMobile={isMobile} />
          <StockTrades symbol={symbol} />

        </>
      )}

      <StockArticle data={stockArticleData} isMobile={isMobile}/>
      <StockLink data={stockLinksData} />
      <StockGraph data={stockGraphData} />

    </div>
  );
}

// Share Holding
// last Graph -> Let's add but let's start with simpler.

export default SymbolPage;
